import React, { useState, useEffect, useMemo, useRef } from "react";
import { apiFetch, fmt, can, canAny, ROLE_OPTIONS } from "../api";
import { Spinner, EmptyState } from "../components/ui";  // UI-SWEEP-ADMIN
import EditCompanyModal from "../components/EditCompanyModal";
import EditContactModal from "../components/EditContactModal";
import { SortableTh, sortRows } from "../components/SortableTable";

// UI-NAV-1.5 (2026-05-07): the System sub-tabs (VCA Data / Audit Log /
// Project Status / Table Editor / System Settings) fold back into Admin
// from the dedicated SystemAdmin compartment.  They were originally
// relocated out by chunk SA-B (2026-05-02) when SystemAdmin was its own
// top-nav peer; the UI Refinement Memo (§4) eliminates that peer in favor
// of folding everything into Admin.  pages/SystemAdmin.js stays alive as
// a route alias for one cycle to handle any in-app callers still using
// setPage("sysadmin"), but the user-visible entry point now lives here.
import SyncAnomaliesPanel from "../components/SyncAnomaliesPanel";
import VcaContactSkippedPanel from "../components/VcaContactSkippedPanel";
import VcaPaymentImport from "../components/VcaPaymentImport";
import AdminFolders from "../components/AdminFolders";  // CLAIM-FILES-1: global folder manager
import ProjectStatusPanel from "../components/ProjectStatusPanel";
import TableEditor from "../components/TableEditor";
import SystemSettings from "../components/SystemSettings";
// UI-NAV-1.6-CLEANUP (2026-05-07): orphan local definitions of AuditLogPanel
// and PipelinePanel removed; both are now imported from components/ which
// SystemAdmin.js was already using.  Drift risk eliminated.
import AuditLogPanel from "../components/AuditLogPanel";
import PipelinePanel from "../components/PipelinePanel";
import WebsitePublishPanel from "../components/WebsitePublishPanel";  // EMPLOYEE-PROFILE-1

// Chunk 14C: legacy ROLES = ["admin", "adjuster", "client-services", "viewer"]
// removed. Role pickers now render ROLE_OPTIONS from api.js which carries the
// 8 canonical role codes + display labels seeded by migrations 014/015.

function Modal({ title, onClose, children, footer }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>{title}</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

function parseErr(e, fallback) {
  try { return JSON.parse(e.message).detail || fallback; } catch { return fallback; }
}

// ===================== USERS TAB =====================
function UsersPanel({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);      // user row being edited
  const [resetting, setResetting] = useState(null);  // user row for password reset
  const [creating, setCreating] = useState(false);
  const [sort, setSort] = useState({ key: "username", dir: "asc" });

  const load = () => { setLoading(true); apiFetch("/users").then(d => { setUsers(d); setLoading(false); }).catch(e => { setErr(parseErr(e, "Failed to load users")); setLoading(false); }); };
  useEffect(() => {
    load();
    // 2026-05-12: refresh every 60s so the Online green-dot column reflects
    // current activity windows without manual refresh.  Cleared on unmount.
    const tick = setInterval(load, 60000);
    return () => clearInterval(tick);
  }, []);

  // Chunk 14E: "Login as" — view the app as another (non-admin, active) user
  // for 30 minutes.  Stashes the admin's original token + user + permissions
  // under original_* keys so the App.js banner can flip back instantly, then
  // reloads the page so the app re-mounts as the impersonated user.
  const loginAs = async (u) => {
    if (!window.confirm(`View the app as ${u.full_name || u.username}?\n\nYour own session will be paused. Click the red banner at the top of the page at any time to return.`)) return;
    try {
      const res = await apiFetch(`/admin/impersonate/${u.id}`, { method: "POST" });
      // Stash originals BEFORE swapping the active token so a refresh failure
      // doesn't leave us in a half-impersonated state.
      const origToken = localStorage.getItem("token");
      const origUser  = localStorage.getItem("user");
      const origPerms = localStorage.getItem("permissions") || "{}";
      localStorage.setItem("original_token", origToken);
      localStorage.setItem("original_user", origUser);
      localStorage.setItem("original_permissions", origPerms);
      // Swap to the impersonation token + target user.
      localStorage.setItem("token", res.access_token);
      localStorage.setItem("user", JSON.stringify(res.user));
      // Refresh capabilities for the impersonated user (uses the new token now).
      try {
        const perms = await apiFetch("/me/permissions");
        localStorage.setItem("permissions", JSON.stringify(perms));
      } catch { localStorage.removeItem("permissions"); }
      window.location.reload();
    } catch (e) {
      setErr(parseErr(e, "Failed to start impersonation"));
    }
  };

  const sortedUsers = useMemo(() => sortRows(users, sort.key, sort.dir), [users, sort]);

  return (
    <div>
      <div className="section-header">
        <span className="section-title">Users ({users.length})</span>
        <button className="btn btn-blue btn-sm" onClick={() => setCreating(true)}>+ New User</button>
      </div>
      {err && <div className="alert-error">{err}</div>}
      <div className="card table-wrap">
        {loading ? <Spinner label="Loading users…" />
         : users.length === 0 ? <EmptyState icon="👤" title="No users" hint="No users have been created yet." />
         : (<table>
            <thead><tr>
              <SortableTh label="ID"        col="id"        sort={sort} setSort={setSort} />
              <SortableTh label="Username"  col="username"  sort={sort} setSort={setSort} />
              <SortableTh label="Full Name" col="full_name" sort={sort} setSort={setSort} />
              <SortableTh label="Email"     col="email"     sort={sort} setSort={setSort} />
              <SortableTh label="Role"      col="role"      sort={sort} setSort={setSort} />
              <SortableTh label="Status"    col="is_active" sort={sort} setSort={setSort} />
              <SortableTh label="Online"    col="last_activity_at" sort={sort} setSort={setSort} />
              <th className="text-right">Actions</th>
            </tr></thead>
            <tbody>{sortedUsers.map(u => (
              <tr key={u.id}>
                <td className="mono" style={{color:"var(--text3)"}}>{u.id}</td>
                <td className="mono" style={{fontWeight:600}}>{u.username}</td>
                <td>{u.full_name || "—"}</td>
                <td>{u.email || "—"}</td>
                <td>
                  {/* Chunk 14D: render every assigned role as a chip.  Falls back
                      to legacy single u.role text for responses that predate the
                      new role_codes array. */}
                  <div style={{display:"flex", flexWrap:"wrap", gap:4}}>
                    {((u.role_codes && u.role_codes.length) ? u.role_codes : [u.role].filter(Boolean)).map(code => {
                      const label = ROLE_OPTIONS.find(r => r.code === code)?.label || code.replace(/_/g, " ");
                      const adminBadge = code === "system_admin" || code === "admin";
                      return (
                        <span key={code} className="badge" style={{
                          background: adminBadge ? "#fee2e2" : "#e0e7ff",
                          color: adminBadge ? "#991b1b" : "#3730a3",
                          fontSize: 10, padding: "2px 6px", textTransform: "capitalize"
                        }}>{label}</span>
                      );
                    })}
                  </div>
                </td>
                <td>{u.is_active ? <span className="badge" style={{background:"#d1fae5",color:"#065f46"}}>Active</span> : <span className="badge" style={{background:"#f3f4f6",color:"#6b7280"}}>Disabled</span>}</td>
                <td>
                  {/* Online indicator — green dot when user is active (is_active=true)
                      AND has any audit_log or phi_access_log activity within the last
                      5 minutes.  Tooltip shows time since last activity for context.
                      Disabled users never show a dot regardless of recent activity. */}
                  {(() => {
                    if (!u.is_active || !u.last_activity_at) return null;
                    const ageMs = Date.now() - new Date(u.last_activity_at).getTime();
                    const onlineWindowMs = 5 * 60 * 1000;
                    if (ageMs > onlineWindowMs || ageMs < 0) return null;
                    const ageSec = Math.max(0, Math.round(ageMs / 1000));
                    const tooltip = ageSec < 60
                      ? `Active ${ageSec}s ago`
                      : `Active ${Math.round(ageSec / 60)}m ago`;
                    return (
                      <span title={tooltip} style={{display:"inline-block",width:10,height:10,borderRadius:"50%",background:"#10b981",boxShadow:"0 0 0 2px rgba(16,185,129,0.18)"}}/>
                    );
                  })()}
                </td>
                <td className="text-right" style={{whiteSpace:"nowrap"}}>
                  <button className="btn btn-outline btn-sm" onClick={() => setEditing({...u})} style={{marginRight:4}}>Edit</button>
                  <button className="btn btn-outline btn-sm" onClick={() => setResetting(u)} style={{marginRight:4}}>Reset PW</button>
                  {/* Chunk 14E "Login as" — only for non-self, active, non-system_admin targets.
                      UsersPanel is already gated on manage_users at the parent (Admin.js sub-tab visibility). */}
                  {currentUser?.id !== u.id && u.is_active && !(u.role_codes || []).includes("system_admin") && (
                    <button className="btn btn-outline btn-sm" onClick={() => loginAs(u)} title={`View as ${u.full_name || u.username} for 30 minutes`}>Login as</button>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>)}
      </div>
      {editing && <EditUserModal user={editing} currentUser={currentUser} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} onDone={() => setResetting(null)} />}
      {creating && <CreateUserModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function EditUserModal({ user, currentUser, onClose, onSaved }) {
  // Chunk 14D: multi-role. Initialize from user.role_codes (authoritative
  // post-14D server) with fallback to [user.role] for any row that predates
  // the new GET /users response shape.
  const initialRoleCodes = (user.role_codes && user.role_codes.length)
    ? [...user.role_codes]
    : [user.role].filter(Boolean);
  // Chunk 17: totp_required + totp_enabled in the form state for the
  // "Require MFA" checkbox + status display.  totp_required defaults to
  // false for any row predating migration 021.
  const [form, setForm] = useState({
    full_name: user.full_name || "",
    email: user.email || "",
    role_codes: initialRoleCodes,
    is_active: user.is_active,
    totp_required: !!user.totp_required,
    // mig 125 — email signature fields + send opt-in gate
    title: user.title || "",
    phone_office: user.phone_office || "",
    phone_direct: user.phone_direct || "",
    phone_mobile: user.phone_mobile || "",
    signature_address: user.signature_address || "",
    email_send_enabled: !!user.email_send_enabled,
    initials: user.initials || "",   // TPA-MATTER-CREATION-1
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const isSelf = currentUser.id === user.id;

  const save = async () => {
    setErr(null); setSaving(true);
    try {
      const patch = {};
      if (form.full_name !== (user.full_name || "")) patch.full_name = form.full_name;
      if (form.email !== (user.email || "")) patch.email = form.email || null;
      // Role-set change detection: sort both arrays, compare stringified.
      const initialSorted = JSON.stringify([...initialRoleCodes].sort());
      const currentSorted = JSON.stringify([...form.role_codes].sort());
      if (currentSorted !== initialSorted) patch.role_codes = form.role_codes;
      if (form.is_active !== user.is_active) patch.is_active = form.is_active;
      // Chunk 17: only send totp_required when it actually changed.  Self-edit
      // hides the control, so this branch is unreachable for self anyway.
      if (form.totp_required !== !!user.totp_required) patch.totp_required = form.totp_required;
      // mig 125 — signature fields (send "" to clear) + send gate
      ["title","phone_office","phone_direct","phone_mobile","signature_address","initials"].forEach(k => {
        if (form[k] !== (user[k] || "")) patch[k] = form[k];
      });
      if (form.email_send_enabled !== !!user.email_send_enabled) patch.email_send_enabled = form.email_send_enabled;
      if (Object.keys(patch).length === 0) { onClose(); return; }
      await apiFetch(`/users/${user.id}`, { method: "PATCH", body: patch });
      onSaved();
    } catch (e) { setErr(parseErr(e, "Failed to save")); setSaving(false); }
  };

  const disable = async () => {
    if (!window.confirm(`Disable user "${user.username}"? They will not be able to log in. You can re-enable them later.`)) return;
    setErr(null); setSaving(true);
    try { await apiFetch(`/users/${user.id}`, { method: "DELETE" }); onSaved(); }
    catch (e) { setErr(parseErr(e, "Failed to disable user")); setSaving(false); }
  };

  const hardDelete = async () => {
    if (!window.confirm(`Permanently delete "${user.username}"? This cannot be undone. If they have any activity in the system, the deletion will be blocked.`)) return;
    setErr(null); setSaving(true);
    try { await apiFetch(`/users/${user.id}?hard=true`, { method: "DELETE" }); onSaved(); }
    catch (e) { setErr(parseErr(e, "Failed to delete user")); setSaving(false); }
  };

  // Chunk 16D — admin-side MFA reset (substitute for backup codes).
  // Used when a user loses their authenticator: clears totp_secret + flag,
  // user re-enrolls fresh via Profile on next login.  Server 400s if the
  // target wasn't actually enrolled — we surface that message verbatim.
  const resetMfa = async () => {
    if (!window.confirm(`Reset two-factor authentication for "${user.username}"? They'll be able to log in with just their password until they re-enroll, so coordinate with them before doing this.`)) return;
    setErr(null); setSaving(true);
    try {
      await apiFetch(`/users/${user.id}/mfa/disable`, { method: "POST", body: {} });
      onSaved();
    } catch (e) {
      setErr(parseErr(e, "Failed to reset MFA"));
      setSaving(false);
    }
  };

  return (
    <Modal title={`Edit User: ${user.username}`} onClose={onClose} footer={
      <>
        {!isSelf && user.is_active  && <button className="btn btn-red btn-sm"                                         onClick={disable}    disabled={saving} style={{marginRight:"auto"}}>Disable User</button>}
        {!isSelf && !user.is_active && <button className="btn btn-sm" style={{background:"#991b1b",color:"#fff",marginRight:"auto"}} onClick={hardDelete} disabled={saving}>Delete Permanently</button>}
        {!isSelf && <button className="btn btn-outline btn-sm" onClick={resetMfa} disabled={saving} title="Clear this user's MFA secret. Use when they've lost their authenticator.">Reset MFA</button>}
        <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || form.role_codes.length === 0}>{saving?"Saving...":"Save"}</button>
      </>
    }>
      {err && <div className="alert-error">{err}</div>}
      <div className="form-grid">
        <div className="form-group"><label>Username (read-only)</label><input value={user.username} disabled style={{background:"var(--bg3)"}}/></div>
        <div className="form-group full"><label>Roles *</label>
          <div style={{display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:"8px 16px", marginTop:6}}>
            {ROLE_OPTIONS.map(r => (
              <label key={r.code} style={{display:"flex", alignItems:"center", gap:8, fontSize:13, lineHeight:1.3, cursor:"pointer", fontWeight:400, textTransform:"none", letterSpacing:"normal", color:"var(--text1)"}}>
                <input type="checkbox"
                       style={{width:16, height:16, minWidth:16, margin:0, padding:0, flexShrink:0, cursor:"pointer", accentColor:"var(--accent, #2563eb)"}}
                       checked={form.role_codes.includes(r.code)}
                       onChange={e => {
                         const next = e.target.checked ? [...form.role_codes, r.code] : form.role_codes.filter(c => c !== r.code);
                         setForm({...form, role_codes: next});
                       }} />
                <span>{r.label}</span>
              </label>
            ))}
          </div>
          {form.role_codes.length === 0 && (
            <div style={{color:"#ef4444", fontSize:11, marginTop:4}}>At least one role required.</div>
          )}
        </div>
        <div className="form-group full"><label>Full Name</label><input value={form.full_name} onChange={e=>setForm({...form,full_name:e.target.value})}/></div>
        <div className="form-group full"><label>Email</label><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></div>
        <div style={{gridColumn:"1 / -1", display:"flex", alignItems:"center", gap:8, margin:"4px 0"}}>
          <input type="checkbox" checked={form.is_active} onChange={e=>setForm({...form,is_active:e.target.checked})} id="is_active_chk" disabled={isSelf}/>
          <label htmlFor="is_active_chk" style={{cursor:isSelf?"not-allowed":"pointer",fontSize:13,fontWeight:400,textTransform:"none",letterSpacing:"normal",color:"var(--text1)"}}>Account Active{isSelf && " (cannot disable your own account)"}</label>
        </div>
        {/* Chunk 17 — Require MFA toggle.  Hidden when editing self (server
            also rejects self-modify).  Status indicator next to the label
            tells the admin whether the target has actually enrolled yet —
            checking the box on a not-yet-enrolled user puts them into
            forced-enrollment on next login. */}
        {!isSelf && (
          <div style={{gridColumn:"1 / -1", display:"flex", alignItems:"center", gap:8, margin:"4px 0"}}>
            <input type="checkbox" checked={form.totp_required} onChange={e=>setForm({...form,totp_required:e.target.checked})} id="totp_required_chk"/>
            <label htmlFor="totp_required_chk" style={{cursor:"pointer",fontSize:13,fontWeight:400,textTransform:"none",letterSpacing:"normal",color:"var(--text1)"}}>Require MFA</label>
            <span style={{fontSize:11, color:"var(--text3)", marginLeft:4}}>
              ({user.totp_enabled ? "✓ currently enrolled" : "not enrolled — will be forced to enroll on next login"})
            </span>
          </div>
        )}

        {/* mig 125 — email signature fields. Company name/website/logo/disclaimer
            come from firm branding; these are the per-user variables. Users can
            also self-edit these in their Profile. */}
        <div className="form-group full" style={{borderTop:"1px solid var(--border)", paddingTop:12, marginTop:4}}>
          <label>Initials <span style={{fontWeight:400,color:"var(--text3)"}}>(TPA claim numbers)</span></label>
          <input value={form.initials} maxLength={4}
            placeholder="derived from name if blank"
            style={{textTransform:"uppercase", maxWidth:120}}
            onChange={e=>setForm({...form,initials:e.target.value.toUpperCase()})}/>
          <div style={{fontSize:11, color:"var(--text3)", marginTop:4}}>Stamped into new TPA matter numbers (YY-T#####<b>{form.initials || "‹initials›"}</b>) when this user is the lead adjuster. Blank ⇒ first+last initial of the name. Editing this only affects matters created afterward.</div>
        </div>
        <div className="form-group full" style={{borderTop:"1px solid var(--border)", paddingTop:12, marginTop:4}}>
          <label style={{fontWeight:600, fontSize:13}}>Email signature</label>
          <div style={{fontSize:11, color:"var(--text3)", marginBottom:8}}>Per-user fields for the firm signature template. Blank phone lines are hidden. The user can also edit these in their own Profile.</div>
        </div>
        <div className="form-group full"><label>Title</label><input value={form.title} placeholder="e.g. Claims Manager" onChange={e=>setForm({...form,title:e.target.value})}/></div>
        <div className="form-group"><label>Direct phone</label><input value={form.phone_direct} onChange={e=>setForm({...form,phone_direct:e.target.value})}/></div>
        <div className="form-group"><label>Office phone</label><input value={form.phone_office} onChange={e=>setForm({...form,phone_office:e.target.value})}/></div>
        <div className="form-group"><label>Mobile phone</label><input value={form.phone_mobile} onChange={e=>setForm({...form,phone_mobile:e.target.value})}/></div>
        <div className="form-group full"><label>Address override</label><input value={form.signature_address} placeholder="4017 13th Ave W, Seattle, WA 98119 (firm default)" onChange={e=>setForm({...form,signature_address:e.target.value})}/></div>

        {/* Email send gate (opt-in, default OFF). Admin-only — never self-service. */}
        <div style={{gridColumn:"1 / -1", display:"flex", alignItems:"center", gap:8, margin:"6px 0", padding:"8px 10px", background: form.email_send_enabled ? "#ecfdf5" : "var(--bg3)", borderRadius:6}}>
          <input type="checkbox" checked={form.email_send_enabled} onChange={e=>setForm({...form,email_send_enabled:e.target.checked})} id="email_send_chk"/>
          <label htmlFor="email_send_chk" style={{cursor:"pointer",fontSize:13,fontWeight:500,textTransform:"none",letterSpacing:"normal",color:"var(--text1)"}}>Allow sending email from Atticus</label>
          <span style={{fontSize:11, color:"var(--text3)", marginLeft:4}}>
            ({form.email_send_enabled ? "enabled" : "off — user cannot send email until enabled"})
          </span>
        </div>
      </div>

      <div style={{borderTop:"1px solid var(--border)", paddingTop:12, marginTop:16}}>
        <div style={{fontWeight:600, fontSize:13, marginBottom:4}}>Personal Rate Cards</div>
        <div style={{fontSize:12, color:"var(--text3)", marginBottom:10}}>
          Per-user billing rates. On unlocked claims, a user's personal rate card takes precedence over the schedule rate. Users without personal cards fall back to the claim's schedule rate. Changes save immediately.
        </div>
        <PersonalRateCardsEditor userId={user.id} userActive={user.is_active}/>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const save = async () => {
    setErr(null);
    if (!pw.next || !pw.confirm) { setErr("Both fields are required"); return; }
    if (pw.next !== pw.confirm) { setErr("Passwords do not match"); return; }
    if (pw.next.length < 12) { setErr("Password must be at least 12 characters"); return; }
    setSaving(true);
    try { await apiFetch(`/users/${user.id}/password`, { method:"POST", body: { new_password: pw.next } }); setDone(true); }
    catch (e) { setErr(parseErr(e, "Failed to reset password")); setSaving(false); }
  };

  return (
    <Modal title={`Reset Password: ${user.username}`} onClose={onClose} footer={
      done ? <button className="btn btn-primary btn-sm" onClick={onDone}>Close</button>
      : <>
        <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving?"Saving...":"Reset Password"}</button>
      </>
    }>
      {err && <div className="alert-error">{err}</div>}
      {done ? (
        <div style={{padding:8, fontSize:13}}>
          <div style={{color:"#065f46", fontWeight:600, marginBottom:8}}>Password reset successfully.</div>
          <div style={{color:"var(--text2)"}}>Share the new password with <strong>{user.username}</strong> through a secure channel (do not email). They should change it on next login.</div>
        </div>
      ) : (<>
        <div style={{fontSize:12, color:"var(--text2)", marginBottom:12}}>Setting a new password for <strong>{user.full_name || user.username}</strong>. They will not receive an automatic notification — communicate the new password to them securely.</div>
        <div className="form-group" style={{marginBottom:10}}><label>New Password</label><input type="password" value={pw.next} onChange={e=>setPw({...pw,next:e.target.value})} autoComplete="new-password"/></div>
        <div className="form-group"><label>Confirm New Password</label><input type="password" value={pw.confirm} onChange={e=>setPw({...pw,confirm:e.target.value})} autoComplete="new-password"/></div>
        <div style={{marginTop:8, fontSize:11, color:"var(--text3)"}}>Minimum 12 characters.</div>
      </>)}
    </Modal>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  // Chunk 14D: multi-role — default a new user to single "adjuster" role.
  const [form, setForm] = useState({ username:"", password:"", confirm:"", full_name:"", email:"", role_codes: ["adjuster"] });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setErr(null);
    if (!form.username.trim() || !form.password || !form.full_name.trim()) { setErr("Username, password, and full name are required"); return; }
    if (form.password !== form.confirm) { setErr("Passwords do not match"); return; }
    if (form.password.length < 12) { setErr("Password must be at least 12 characters"); return; }
    setSaving(true);
    try {
      await apiFetch("/users", { method:"POST", body: { username: form.username.trim(), password: form.password, full_name: form.full_name.trim(), email: form.email.trim() || null, role_codes: form.role_codes } });
      onCreated();
    } catch (e) { setErr(parseErr(e, "Failed to create user")); setSaving(false); }
  };

  return (
    <Modal title="Create New User" onClose={onClose} footer={<>
      <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || form.role_codes.length === 0}>{saving?"Saving...":"Create User"}</button>
    </>}>
      {err && <div className="alert-error">{err}</div>}
      <div className="form-grid">
        <div className="form-group"><label>Username *</label><input value={form.username} onChange={e=>setForm({...form,username:e.target.value})} placeholder="e.g. jsmith" autoFocus/></div>
        <div className="form-group full"><label>Roles *</label>
          <div style={{display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:"8px 16px", marginTop:6}}>
            {ROLE_OPTIONS.map(r => (
              <label key={r.code} style={{display:"flex", alignItems:"center", gap:8, fontSize:13, lineHeight:1.3, cursor:"pointer", fontWeight:400, textTransform:"none", letterSpacing:"normal", color:"var(--text1)"}}>
                <input type="checkbox"
                       style={{width:16, height:16, minWidth:16, margin:0, padding:0, flexShrink:0, cursor:"pointer", accentColor:"var(--accent, #2563eb)"}}
                       checked={form.role_codes.includes(r.code)}
                       onChange={e => {
                         const next = e.target.checked ? [...form.role_codes, r.code] : form.role_codes.filter(c => c !== r.code);
                         setForm({...form, role_codes: next});
                       }} />
                <span>{r.label}</span>
              </label>
            ))}
          </div>
          {form.role_codes.length === 0 && (
            <div style={{color:"#ef4444", fontSize:11, marginTop:4}}>At least one role required.</div>
          )}
        </div>
        <div className="form-group full"><label>Full Name *</label><input value={form.full_name} onChange={e=>setForm({...form,full_name:e.target.value})} placeholder="First Last"/></div>
        <div className="form-group full"><label>Email</label><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="name@company.com"/></div>
        <div className="form-group"><label>Password *</label><input type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} autoComplete="new-password"/></div>
        <div className="form-group"><label>Confirm Password *</label><input type="password" value={form.confirm} onChange={e=>setForm({...form,confirm:e.target.value})} autoComplete="new-password"/></div>
      </div>
      <div style={{marginTop:10, fontSize:11, color:"var(--text3)"}}>Minimum 12 characters. Communicate the password to the user securely.</div>
    </Modal>
  );
}

// ===================== BILLING RATES TAB =====================
function BillingRatesPanel() {
  const [rates, setRates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const userMap = users.reduce((acc, u) => { acc[u.id] = u; return acc; }, {});

  const load = () => {
    setLoading(true);
    Promise.all([apiFetch("/billing-rates"), apiFetch("/users")])
      .then(([r, u]) => { setRates(r); setUsers(u); setLoading(false); })
      .catch(e => { setErr(parseErr(e, "Failed to load rates")); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="section-header">
        <span className="section-title">Billing Rates ({rates.length})</span>
        <button className="btn btn-blue btn-sm" onClick={() => setCreating(true)}>+ New Rate</button>
      </div>
      <div style={{fontSize:12, color:"var(--text3)", marginBottom:10}}>
        Global rates apply to everyone unless a user has a personal rate card. Add personal rate cards from the user's Edit panel.
      </div>
      {err && <div className="alert-error">{err}</div>}
      <div className="card table-wrap">
        {loading ? <Spinner label="Loading rates…" />
         : rates.length === 0 ? <EmptyState icon="💰" title="No billing rates yet" hint="Create the first rate with + New Rate." />
         : (<table>
            <thead><tr><th>Code</th><th>Description</th><th>Type</th><th>User</th><th className="text-right">Default Rate</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
            <tbody>{rates.map(r => (
              <tr key={r.id}>
                <td className="mono" style={{fontWeight:600}}>{r.rate_code}</td>
                <td>{r.description || "—"}</td>
                <td>{r.rate_type || "—"}</td>
                <td>{r.user_id
                    ? (userMap[r.user_id]?.full_name || userMap[r.user_id]?.username || `User #${r.user_id}`)
                    : <span style={{fontStyle:"italic",color:"var(--text3)"}}>— Global</span>}</td>
                <td className="text-right mono">{fmt.money(r.default_rate)}/hr</td>
                <td>{r.is_active ? <span className="badge" style={{background:"#d1fae5",color:"#065f46"}}>Active</span> : <span className="badge" style={{background:"#f3f4f6",color:"#6b7280"}}>Disabled</span>}</td>
                <td className="text-right"><button className="btn btn-outline btn-sm" onClick={() => setEditing({...r})}>Edit</button></td>
              </tr>
            ))}</tbody>
          </table>)}
      </div>
      {editing && <EditRateModal rate={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
      {creating && <CreateRateModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }}/>}
    </div>
  );
}

function EditRateModal({ rate, onClose, onSaved }) {
  const [form, setForm] = useState({ description: rate.description || "", rate_type: rate.rate_type || "", default_rate: String(rate.default_rate ?? ""), is_active: rate.is_active });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setErr(null); setSaving(true);
    try {
      const patch = {};
      if (form.description !== (rate.description || "")) patch.description = form.description;
      if (form.rate_type !== (rate.rate_type || "")) patch.rate_type = form.rate_type;
      if (parseFloat(form.default_rate) !== parseFloat(rate.default_rate)) patch.default_rate = parseFloat(form.default_rate);
      if (form.is_active !== rate.is_active) patch.is_active = form.is_active;
      if (Object.keys(patch).length === 0) { onClose(); return; }
      await apiFetch(`/billing-rates/${rate.id}`, { method: "PATCH", body: patch });
      onSaved();
    } catch (e) { setErr(parseErr(e, "Failed to save")); setSaving(false); }
  };

  return (
    <Modal title={`Edit Billing Rate: ${rate.rate_code}`} onClose={onClose} footer={<>
      <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving?"Saving...":"Save"}</button>
    </>}>
      {err && <div className="alert-error">{err}</div>}
      <div className="form-grid">
        <div className="form-group"><label>Rate Code (read-only)</label><input value={rate.rate_code} disabled style={{background:"var(--bg3)"}}/></div>
        <div className="form-group"><label>Rate Type</label><input value={form.rate_type} onChange={e=>setForm({...form,rate_type:e.target.value})} placeholder="e.g. Adjuster, Client Services"/></div>
        <div className="form-group full"><label>Description</label><input value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
        <div className="form-group"><label>Default Rate ($/hr)</label><input type="number" step="0.01" value={form.default_rate} onChange={e=>setForm({...form,default_rate:e.target.value})}/></div>
        <div style={{display:"flex", alignItems:"center", gap:8, alignSelf:"end", paddingBottom:6}}>
          <input type="checkbox" checked={form.is_active} onChange={e=>setForm({...form,is_active:e.target.checked})} id="rate_active"/>
          <label htmlFor="rate_active" style={{cursor:"pointer",fontSize:13,fontWeight:400,textTransform:"none",letterSpacing:"normal",color:"var(--text1)"}}>Active</label>
        </div>
      </div>
    </Modal>
  );
}

function CreateRateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ rate_code:"", description:"", rate_type:"", default_rate:"" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setErr(null);
    if (!form.rate_code.trim()) { setErr("Rate code is required"); return; }
    const rate = parseFloat(form.default_rate);
    if (isNaN(rate) || rate < 0) { setErr("Default rate must be a non-negative number"); return; }
    setSaving(true);
    try {
      await apiFetch("/billing-rates", { method:"POST", body: { rate_code: form.rate_code.trim().toUpperCase(), description: form.description || null, rate_type: form.rate_type || null, default_rate: rate } });
      onCreated();
    } catch (e) { setErr(parseErr(e, "Failed to create rate")); setSaving(false); }
  };

  return (
    <Modal title="Create New Billing Rate" onClose={onClose} footer={<>
      <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving?"Saving...":"Create Rate"}</button>
    </>}>
      {err && <div className="alert-error">{err}</div>}
      <div className="form-grid">
        <div className="form-group"><label>Rate Code *</label><input value={form.rate_code} onChange={e=>setForm({...form,rate_code:e.target.value})} placeholder="e.g. SR_ADJ" autoFocus style={{textTransform:"uppercase"}}/></div>
        <div className="form-group"><label>Default Rate ($/hr) *</label><input type="number" step="0.01" value={form.default_rate} onChange={e=>setForm({...form,default_rate:e.target.value})} placeholder="175.00"/></div>
        <div className="form-group full"><label>Description</label><input value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Senior Adjuster"/></div>
        <div className="form-group full"><label>Rate Type</label><input value={form.rate_type} onChange={e=>setForm({...form,rate_type:e.target.value})} placeholder="Adjuster"/></div>
      </div>
      <div style={{marginTop:10, fontSize:11, color:"var(--text3)"}}>Rate code is a short unique identifier (uppercased). Used internally to reference the rate.</div>
    </Modal>
  );
}

// ===================== PERSONAL RATE CARDS (embedded in EditUserModal) =====================
function PersonalRateCardsEditor({ userId, userActive }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [newCard, setNewCard] = useState({ rate_code:"", description:"", rate_type:"", rate:"" });
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState(null);

  const load = () => {
    setLoading(true);
    apiFetch(`/billing-rates?user_id=${userId}`)
      .then(d => { setCards(d); setLoading(false); })
      .catch(e => { setLoadErr(parseErr(e, "Failed to load personal rate cards")); setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const addCard = async () => {
    setAddErr(null);
    if (!newCard.rate_code.trim()) { setAddErr("Rate code is required"); return; }
    const rate = parseFloat(newCard.rate);
    if (isNaN(rate) || rate < 0) { setAddErr("Rate must be a non-negative number"); return; }
    setAdding(true);
    try {
      await apiFetch("/billing-rates", { method:"POST", body: {
        rate_code: newCard.rate_code.trim().toUpperCase(),
        description: newCard.description.trim() || null,
        rate_type: newCard.rate_type.trim() || null,
        default_rate: rate,
        user_id: userId,
      }});
      setNewCard({ rate_code:"", description:"", rate_type:"", rate:"" });
      load();
    } catch (e) { setAddErr(parseErr(e, "Failed to add card")); }
    finally { setAdding(false); }
  };

  if (loading) return <div className="loading">Loading personal rate cards...</div>;

  return (
    <div>
      {loadErr && <div className="alert-error">{loadErr}</div>}
      {cards.length === 0 ? (
        <div style={{fontSize:12, color:"var(--text3)", padding:"6px 0 10px"}}>
          No personal rate cards. Adjusters without personal cards use the claim's schedule rate.
        </div>
      ) : (
        <table style={{marginBottom:12}}>
          <thead><tr><th>Code</th><th>Description</th><th>Type</th><th className="text-right">$/hr</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
          <tbody>{cards.map(c => <PersonalRateCardRow key={c.id} card={c} onChanged={load}/>)}</tbody>
        </table>
      )}
      {!userActive ? (
        <div style={{fontSize:11, color:"var(--text3)", fontStyle:"italic", padding:"6px 0"}}>
          User is disabled — re-enable the account to add new rate cards.
        </div>
      ) : (
        <div style={{padding:"10px", background:"var(--bg3)", borderRadius:6}}>
          <div style={{display:"flex", gap:8, alignItems:"flex-end", flexWrap:"wrap"}}>
            <div style={{width:120}}>
              <label style={{fontSize:11, color:"var(--text3)", display:"block", marginBottom:4}}>Code *</label>
              <input value={newCard.rate_code} onChange={e=>setNewCard({...newCard,rate_code:e.target.value})} placeholder="ERIN_SPEC" style={{width:"100%",textTransform:"uppercase"}}/>
            </div>
            <div style={{flex:"1 1 160px"}}>
              <label style={{fontSize:11, color:"var(--text3)", display:"block", marginBottom:4}}>Description</label>
              <input value={newCard.description} onChange={e=>setNewCard({...newCard,description:e.target.value})} placeholder="Specialty Adjusting" style={{width:"100%"}}/>
            </div>
            <div style={{width:130}}>
              <label style={{fontSize:11, color:"var(--text3)", display:"block", marginBottom:4}}>Type</label>
              <input value={newCard.rate_type} onChange={e=>setNewCard({...newCard,rate_type:e.target.value})} placeholder="Adjuster" style={{width:"100%"}}/>
            </div>
            <div style={{width:100}}>
              <label style={{fontSize:11, color:"var(--text3)", display:"block", marginBottom:4}}>$/hr *</label>
              <input type="number" step="0.01" value={newCard.rate} onChange={e=>setNewCard({...newCard,rate:e.target.value})} placeholder="185.00" style={{width:"100%"}}/>
            </div>
            <button className="btn btn-blue btn-sm" onClick={addCard} disabled={adding}>{adding?"Adding...":"+ Add"}</button>
          </div>
          {addErr && <div className="alert-error" style={{marginTop:8}}>{addErr}</div>}
        </div>
      )}
    </div>
  );
}

function PersonalRateCardRow({ card, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    description: card.description || "",
    rate_type: card.rate_type || "",
    rate: String(card.default_rate),
    is_active: card.is_active,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const reset = () => {
    setForm({ description: card.description || "", rate_type: card.rate_type || "", rate: String(card.default_rate), is_active: card.is_active });
    setErr(null);
  };

  const save = async () => {
    setErr(null);
    const rate = parseFloat(form.rate);
    if (isNaN(rate) || rate < 0) { setErr("Rate must be a non-negative number"); return; }
    setSaving(true);
    try {
      const patch = {};
      if (form.description !== (card.description || "")) patch.description = form.description;
      if (form.rate_type !== (card.rate_type || "")) patch.rate_type = form.rate_type;
      if (rate !== parseFloat(card.default_rate)) patch.default_rate = rate;
      if (form.is_active !== card.is_active) patch.is_active = form.is_active;
      if (Object.keys(patch).length > 0) {
        await apiFetch(`/billing-rates/${card.id}`, { method:"PATCH", body: patch });
      }
      setEditing(false);
      onChanged();
    } catch (e) { setErr(parseErr(e, "Failed to save")); }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <tr>
        <td className="mono" style={{fontWeight:600}}>{card.rate_code}</td>
        <td><input value={form.description} onChange={e=>setForm({...form,description:e.target.value})} style={{width:"100%"}}/></td>
        <td><input value={form.rate_type} onChange={e=>setForm({...form,rate_type:e.target.value})} style={{width:"100%"}}/></td>
        <td className="text-right"><input type="number" step="0.01" value={form.rate} onChange={e=>setForm({...form,rate:e.target.value})} style={{width:90,textAlign:"right"}}/></td>
        <td>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <input type="checkbox" checked={form.is_active} onChange={e=>setForm({...form,is_active:e.target.checked})} id={`card_active_${card.id}`}/>
            <label htmlFor={`card_active_${card.id}`} style={{cursor:"pointer",fontSize:12,fontWeight:400,textTransform:"none",letterSpacing:"normal",color:"var(--text1)"}}>Active</label>
          </div>
        </td>
        <td className="text-right" style={{whiteSpace:"nowrap"}}>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving} style={{marginRight:4}}>{saving?"...":"Save"}</button>
          <button className="btn btn-outline btn-sm" onClick={() => { reset(); setEditing(false); }} disabled={saving}>Cancel</button>
          {err && <div style={{color:"#991b1b",fontSize:11,marginTop:4}}>{err}</div>}
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="mono" style={{fontWeight:600}}>{card.rate_code}</td>
      <td>{card.description || "—"}</td>
      <td>{card.rate_type || "—"}</td>
      <td className="text-right mono">{fmt.money(card.default_rate)}/hr</td>
      <td>{card.is_active ? <span className="badge" style={{background:"#d1fae5",color:"#065f46"}}>Active</span> : <span className="badge" style={{background:"#f3f4f6",color:"#6b7280"}}>Disabled</span>}</td>
      <td className="text-right" style={{whiteSpace:"nowrap"}}>
        <button className="btn btn-outline btn-sm" onClick={() => setEditing(true)}>Edit</button>
        {err && <div style={{color:"#991b1b",fontSize:11,marginTop:4}}>{err}</div>}
      </td>
    </tr>
  );
}

// ===================== RATE SCHEDULES TAB =====================
function RateSchedulesPanel() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => { setLoading(true); apiFetch("/rate-schedules?include_inactive=true").then(d => { setSchedules(d); setLoading(false); }).catch(e => { setErr(parseErr(e, "Failed to load schedules")); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="section-header">
        <span className="section-title">Rate Schedules ({schedules.length})</span>
        <button className="btn btn-blue btn-sm" onClick={() => setCreating(true)}>+ New Schedule</button>
      </div>
      <div style={{fontSize:12, color:"var(--text3)", marginBottom:10}}>
        Named bundles of rates by role (e.g. "Hartford MSA", "WNW Standard"). A claim can reference a schedule to default its billing rates. When a schedule is <strong>Locked</strong>, adjusters can't override the rate on time entries against that claim.
      </div>
      {err && <div className="alert-error">{err}</div>}
      <div className="card table-wrap">
        {loading ? <Spinner label="Loading schedules…" />
         : schedules.length === 0 ? <EmptyState icon="📋" title="No rate schedules yet" hint="Create the first schedule with + New Schedule." />
         : (<table>
            <thead><tr><th>Name</th><th>Description</th><th>Locked</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
            <tbody>{schedules.map(s => (
              <tr key={s.id}>
                <td className="mono" style={{fontWeight:600}}>{s.name}</td>
                <td>{s.description || "—"}</td>
                <td>{s.locks_rate ? <span className="badge" style={{background:"#fee2e2",color:"#991b1b"}}>Locked</span> : <span style={{color:"var(--text3)"}}>—</span>}</td>
                <td>{s.is_active ? <span className="badge" style={{background:"#d1fae5",color:"#065f46"}}>Active</span> : <span className="badge" style={{background:"#f3f4f6",color:"#6b7280"}}>Disabled</span>}</td>
                <td className="text-right"><button className="btn btn-outline btn-sm" onClick={() => setEditing(s)}>Edit</button></td>
              </tr>
            ))}</tbody>
          </table>)}
      </div>
      {editing && <EditScheduleModal schedule={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
      {creating && <CreateScheduleModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }}/>}
    </div>
  );
}

function CreateScheduleModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name:"", description:"", locks_rate: false });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setErr(null);
    if (!form.name.trim()) { setErr("Name is required"); return; }
    setSaving(true);
    try {
      await apiFetch("/rate-schedules", { method:"POST", body: { name: form.name.trim(), description: form.description.trim() || null, locks_rate: form.locks_rate } });
      onCreated();
    } catch (e) { setErr(parseErr(e, "Failed to create schedule")); setSaving(false); }
  };

  return (
    <Modal title="Create New Rate Schedule" onClose={onClose} footer={<>
      <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving?"Saving...":"Create Schedule"}</button>
    </>}>
      {err && <div className="alert-error">{err}</div>}
      <div className="form-grid">
        <div className="form-group full"><label>Name *</label><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="e.g. Hartford MSA, WNW Standard" autoFocus/></div>
        <div className="form-group full"><label>Description</label><input value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Optional — what's this schedule for?"/></div>
        <div style={{gridColumn:"1 / -1", display:"flex", alignItems:"center", gap:8, margin:"4px 0"}}>
          <input type="checkbox" checked={form.locks_rate} onChange={e=>setForm({...form,locks_rate:e.target.checked})} id="new_sched_locks"/>
          <label htmlFor="new_sched_locks" style={{cursor:"pointer",fontSize:13,fontWeight:400,textTransform:"none",letterSpacing:"normal",color:"var(--text1)"}}>Locked — adjusters cannot override rates on claims using this schedule (use for MSAs)</label>
        </div>
      </div>
      <div style={{marginTop:10, fontSize:11, color:"var(--text3)"}}>After creating, click Edit to add rate items (e.g. Adjuster $160, Claims Support $110).</div>
    </Modal>
  );
}

function EditScheduleModal({ schedule, onClose, onSaved }) {
  const [form, setForm] = useState({ name: schedule.name, description: schedule.description || "", locks_rate: schedule.locks_rate, is_active: schedule.is_active });
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemErr, setItemErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const loadItems = () => {
    setItemsLoading(true);
    apiFetch(`/rate-schedules/${schedule.id}`)
      .then(d => { setItems(d.items || []); setItemsLoading(false); })
      .catch(e => { setItemErr(parseErr(e, "Failed to load items")); setItemsLoading(false); });
  };
  useEffect(() => { loadItems(); /* eslint-disable-next-line */ }, [schedule.id]);

  const saveMeta = async () => {
    setErr(null);
    if (!form.name.trim()) { setErr("Name is required"); return; }
    setSaving(true);
    try {
      const patch = {};
      if (form.name.trim() !== schedule.name) patch.name = form.name.trim();
      if (form.description !== (schedule.description || "")) patch.description = form.description;
      if (form.locks_rate !== schedule.locks_rate) patch.locks_rate = form.locks_rate;
      if (form.is_active !== schedule.is_active) patch.is_active = form.is_active;
      if (Object.keys(patch).length === 0) { onSaved(); return; }
      await apiFetch(`/rate-schedules/${schedule.id}`, { method: "PATCH", body: patch });
      onSaved();
    } catch (e) { setErr(parseErr(e, "Failed to save")); setSaving(false); }
  };

  return (
    <Modal title={`Edit Rate Schedule: ${schedule.name}`} onClose={onClose} footer={<>
      <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={saveMeta} disabled={saving}>{saving?"Saving...":"Save Details"}</button>
    </>}>
      {err && <div className="alert-error">{err}</div>}
      <div className="form-grid" style={{marginBottom:16}}>
        <div className="form-group full"><label>Name</label><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
        <div className="form-group full"><label>Description</label><input value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
        <div style={{gridColumn:"1 / -1", display:"flex", alignItems:"center", gap:8, margin:"4px 0"}}>
          <input type="checkbox" checked={form.locks_rate} onChange={e=>setForm({...form,locks_rate:e.target.checked})} id={`locks_${schedule.id}`}/>
          <label htmlFor={`locks_${schedule.id}`} style={{cursor:"pointer",fontSize:13,fontWeight:400,textTransform:"none",letterSpacing:"normal",color:"var(--text1)"}}>Locked — adjusters cannot override rates (MSAs)</label>
        </div>
        <div style={{gridColumn:"1 / -1", display:"flex", alignItems:"center", gap:8, margin:"4px 0"}}>
          <input type="checkbox" checked={form.is_active} onChange={e=>setForm({...form,is_active:e.target.checked})} id={`active_${schedule.id}`}/>
          <label htmlFor={`active_${schedule.id}`} style={{cursor:"pointer",fontSize:13,fontWeight:400,textTransform:"none",letterSpacing:"normal",color:"var(--text1)"}}>Active</label>
        </div>
      </div>

      <div style={{borderTop:"1px solid var(--border)", paddingTop:12}}>
        <div style={{fontWeight:600, fontSize:13, marginBottom:4}}>Rate Items</div>
        <div style={{fontSize:12, color:"var(--text3)", marginBottom:10}}>
          One rate per role/type. Each rate type must be unique within this schedule. Changes to items save immediately.
        </div>
        {itemErr && <div className="alert-error">{itemErr}</div>}
        <ScheduleItemsEditor scheduleId={schedule.id} items={items} loading={itemsLoading} onChanged={loadItems}/>
      </div>
    </Modal>
  );
}

function ScheduleItemsEditor({ scheduleId, items, loading, onChanged }) {
  const [newItem, setNewItem] = useState({ rate_type:"", rate:"" });
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState(null);

  const addItem = async () => {
    setAddErr(null);
    if (!newItem.rate_type.trim()) { setAddErr("Rate type is required"); return; }
    const rate = parseFloat(newItem.rate);
    if (isNaN(rate) || rate < 0) { setAddErr("Rate must be a non-negative number"); return; }
    setAdding(true);
    try {
      await apiFetch(`/rate-schedules/${scheduleId}/items`, { method:"POST", body: { rate_type: newItem.rate_type.trim(), rate } });
      setNewItem({ rate_type:"", rate:"" });
      onChanged();
    } catch (e) { setAddErr(parseErr(e, "Failed to add item")); }
    finally { setAdding(false); }
  };

  if (loading) return <div className="loading">Loading items...</div>;

  return (
    <div>
      {items.length === 0 ? (
        <div style={{fontSize:12, color:"var(--text3)", padding:"6px 0 10px"}}>No rate items yet. Add your first one below.</div>
      ) : (
        <table style={{marginBottom:12}}>
          <thead><tr><th>Rate Type</th><th className="text-right">Rate $/hr</th><th className="text-right">Actions</th></tr></thead>
          <tbody>{items.map(it => <ScheduleItemRow key={it.id} item={it} onChanged={onChanged}/>)}</tbody>
        </table>
      )}
      <div style={{display:"flex", gap:8, alignItems:"flex-end", padding:"10px 10px 12px", background:"var(--bg3)", borderRadius:6}}>
        <div style={{flex:1}}>
          <label style={{fontSize:11, color:"var(--text3)", display:"block", marginBottom:4}}>Rate Type</label>
          <input value={newItem.rate_type} onChange={e=>setNewItem({...newItem,rate_type:e.target.value})} placeholder="e.g. Adjuster" style={{width:"100%"}}/>
        </div>
        <div style={{width:130}}>
          <label style={{fontSize:11, color:"var(--text3)", display:"block", marginBottom:4}}>$/hr</label>
          <input type="number" step="0.01" value={newItem.rate} onChange={e=>setNewItem({...newItem,rate:e.target.value})} placeholder="160.00" style={{width:"100%"}}/>
        </div>
        <button className="btn btn-blue btn-sm" onClick={addItem} disabled={adding}>{adding?"Adding...":"+ Add"}</button>
      </div>
      {addErr && <div className="alert-error" style={{marginTop:8}}>{addErr}</div>}
    </div>
  );
}

function ScheduleItemRow({ item, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ rate_type: item.rate_type, rate: String(item.rate) });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setErr(null);
    const rate = parseFloat(form.rate);
    if (!form.rate_type.trim()) { setErr("Rate type required"); return; }
    if (isNaN(rate) || rate < 0) { setErr("Rate must be a non-negative number"); return; }
    setSaving(true);
    try {
      const patch = {};
      if (form.rate_type.trim() !== item.rate_type) patch.rate_type = form.rate_type.trim();
      if (rate !== parseFloat(item.rate)) patch.rate = rate;
      if (Object.keys(patch).length > 0) {
        await apiFetch(`/rate-schedule-items/${item.id}`, { method:"PATCH", body: patch });
      }
      setEditing(false);
      onChanged();
    } catch (e) { setErr(parseErr(e, "Failed to save")); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove rate item "${item.rate_type}" (${fmt.money(item.rate)}/hr) from this schedule?`)) return;
    setSaving(true);
    try {
      await apiFetch(`/rate-schedule-items/${item.id}`, { method:"DELETE" });
      onChanged();
    } catch (e) { setErr(parseErr(e, "Failed to remove")); setSaving(false); }
  };

  if (editing) {
    return (
      <tr>
        <td><input value={form.rate_type} onChange={e=>setForm({...form,rate_type:e.target.value})} style={{width:"100%"}}/></td>
        <td className="text-right"><input type="number" step="0.01" value={form.rate} onChange={e=>setForm({...form,rate:e.target.value})} style={{width:110, textAlign:"right"}}/></td>
        <td className="text-right" style={{whiteSpace:"nowrap"}}>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving} style={{marginRight:4}}>{saving?"...":"Save"}</button>
          <button className="btn btn-outline btn-sm" onClick={() => { setForm({ rate_type: item.rate_type, rate: String(item.rate) }); setEditing(false); setErr(null); }} disabled={saving}>Cancel</button>
          {err && <div style={{color:"#991b1b", fontSize:11, marginTop:4}}>{err}</div>}
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{item.rate_type}</td>
      <td className="text-right mono">{fmt.money(item.rate)}/hr</td>
      <td className="text-right" style={{whiteSpace:"nowrap"}}>
        <button className="btn btn-outline btn-sm" onClick={() => setEditing(true)} style={{marginRight:4}}>Edit</button>
        <button className="btn btn-red btn-sm" onClick={remove} disabled={saving} title="Remove">×</button>
        {err && <div style={{color:"#991b1b", fontSize:11, marginTop:4}}>{err}</div>}
      </td>
    </tr>
  );
}

// ============================================================================
// REVIEW QUEUE TAB (Chunk 7D + 7E)
// ----------------------------------------------------------------------------
// Admin-only triage surface for entities with needs_admin_review = true.
// Currently covers:
//   • Companies (Chunk 7D) — full edit flow via EditCompanyModal.
//   • Contacts  (Chunk 7E) — approve / bulk-approve only (no edit modal yet;
//     contacts CRUD UI comes with the Contacts page later).
//
// For each entity we show two collapsible sections:
//   • "Never reviewed"  — rows with no last_reviewed_snapshot (mostly VCA
//     imports from Phase 1a/1b). Collapsed by default (big pile).
//   • "Edited since last review" — rows with a snapshot that diverges from
//     current values. Expanded by default (small, needs careful eyes).
// Each section supports select-all + bulk approve plus per-row Approve.
// ============================================================================

// Fields the review snapshot tracks — mirrors Companies.js REVIEWABLE_FIELDS.
const REVIEWABLE_FIELDS_Q = ["company_name","city","state","phone","email","notes","default_rate_schedule_id"];
const FIELD_LABELS_Q = {
  company_name: "Name", city: "City", state: "State", phone: "Phone",
  email: "Email", notes: "Notes", default_rate_schedule_id: "Rate Schedule",
};

// Contact-side field labels (mirrors _refresh_contact_review snapshot keys).
const REVIEWABLE_FIELDS_CT = ["first_name","last_name","email","title","phone","phone_mobile","fax","company_id","notes"];
const FIELD_LABELS_CT = {
  first_name: "First", last_name: "Last", email: "Email", title: "Title",
  phone: "Phone", phone_mobile: "Mobile", fax: "Fax", company_id: "Company", notes: "Notes",
};

function normQ(v) { return (v === null || v === undefined || v === "") ? null : v; }
function diffFields(snapshot, row) {
  // Return list of { field, old, new } for every REVIEWABLE_FIELDS_Q key that
  // differs. Snapshot values come straight from jsonb (null-coerced). Swap
  // schedule ids for human names at render time — keep raw here.
  if (!snapshot) return [];
  const out = [];
  for (const f of REVIEWABLE_FIELDS_Q) {
    const s = normQ(snapshot[f]); const c = normQ(row[f]);
    if (s !== c) out.push({ field: f, old: s, new: c });
  }
  return out;
}

// Contact equivalent. Kept separate so field lists stay explicit and the
// snapshot contract isn't coupled across entities.
function diffFieldsContact(snapshot, row) {
  if (!snapshot) return [];
  const out = [];
  for (const f of REVIEWABLE_FIELDS_CT) {
    const s = normQ(snapshot[f]); const c = normQ(row[f]);
    if (s !== c) out.push({ field: f, old: s, new: c });
  }
  return out;
}

function DiffChip({ diff, scheduleMap }) {
  // Render one old→new chip. For schedule ids, substitute the human name so
  // admins see "Standard Schedule" instead of "4".
  const label = FIELD_LABELS_Q[diff.field] || diff.field;
  const fmtVal = (v) => {
    if (v === null) return <em style={{color:"var(--text3)"}}>empty</em>;
    if (diff.field === "default_rate_schedule_id") return scheduleMap[v]?.name || `#${v}`;
    const s = String(v);
    return s.length > 40 ? s.slice(0, 40) + "…" : s;
  };
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:4,background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:4,padding:"2px 6px",fontSize:11,marginRight:4,marginBottom:4}}>
      <strong style={{color:"#92400e"}}>{label}:</strong>
      <span style={{color:"#6b7280",textDecoration:"line-through"}}>{fmtVal(diff.old)}</span>
      <span style={{color:"#92400e"}}>→</span>
      <span style={{color:"#065f46",fontWeight:500}}>{fmtVal(diff.new)}</span>
    </span>
  );
}

// Contact chip — labels from FIELD_LABELS_CT; swap company_id for company name.
function ContactDiffChip({ diff, companyNameFor }) {
  const label = FIELD_LABELS_CT[diff.field] || diff.field;
  const fmtVal = (v) => {
    if (v === null) return <em style={{color:"var(--text3)"}}>empty</em>;
    if (diff.field === "company_id") return companyNameFor(v) || `#${v}`;
    const s = String(v);
    return s.length > 40 ? s.slice(0, 40) + "…" : s;
  };
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:4,background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:4,padding:"2px 6px",fontSize:11,marginRight:4,marginBottom:4}}>
      <strong style={{color:"#92400e"}}>{label}:</strong>
      <span style={{color:"#6b7280",textDecoration:"line-through"}}>{fmtVal(diff.old)}</span>
      <span style={{color:"#92400e"}}>→</span>
      <span style={{color:"#065f46",fontWeight:500}}>{fmtVal(diff.new)}</span>
    </span>
  );
}

// Compose "Last, First" for sort/display; fall back to email if both blank.
function contactDisplayName(c) {
  const full = [c.last_name, c.first_name].filter(Boolean).join(", ");
  return full || c.email || `#${c.id}`;
}

function SectionHeader({ open, onToggle, title, count, hint, rightAction }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:"var(--panel)",border:"1px solid var(--border)",borderRadius:"6px 6px 0 0",borderBottom: open ? "1px solid var(--border)" : "1px solid var(--border)"}}>
      <button className="btn-link" onClick={onToggle} style={{background:"none",border:"none",cursor:"pointer",padding:0,fontSize:14,color:"var(--text1)",display:"flex",alignItems:"center",gap:6}}>
        <span style={{display:"inline-block",width:12,transform:open?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.15s"}}>▶</span>
        <strong>{title}</strong>
        <span style={{color:"var(--text2)",fontWeight:400}}>({count})</span>
      </button>
      {hint && <span style={{fontSize:11,color:"var(--text3)",marginLeft:8}}>{hint}</span>}
      <div style={{marginLeft:"auto"}}>{rightAction}</div>
    </div>
  );
}

function ReviewQueuePanel({ currentUser, onApproved, onNavigateToClaim, onNavigateToContact }) {
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts]   = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Companies sections
  const [coNeverOpen, setCoNeverOpen] = useState(false);  // collapsed — big VCA pile
  const [coEditsOpen, setCoEditsOpen] = useState(true);   // expanded — small + urgent
  const [selCoNever, setSelCoNever] = useState(() => new Set());
  const [selCoEdits, setSelCoEdits] = useState(() => new Set());
  // Contacts sections (Chunk 7E)
  const [ctNeverOpen, setCtNeverOpen] = useState(false);
  const [ctEditsOpen, setCtEditsOpen] = useState(true);
  const [selCtNever, setSelCtNever] = useState(() => new Set());
  const [selCtEdits, setSelCtEdits] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);       // company row being edited via the shared modal
  const [editingContact, setEditingContact] = useState(null); // Chunk 7F — contact row being edited
  // Chunk 14C: isAdmin name kept for prop-pass stability into
  // EditCompanyModal / EditContactModal; value is now the approve capability.
  const isAdmin = can("approve_companies");

  const load = () => {
    setLoading(true);
    Promise.all([
      apiFetch("/companies?needs_review=true&include_inactive=true"),
      apiFetch("/contacts?needs_review=true"),
      apiFetch("/rate-schedules"),
    ]).then(([co, ct, s]) => {
      setCompanies(co); setContacts(ct); setSchedules(s);
      setSelCoNever(new Set()); setSelCoEdits(new Set());
      setSelCtNever(new Set()); setSelCtEdits(new Set());
      setLoading(false);
    }).catch(e => { setErr(parseErr(e, "Failed to load review queue")); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const scheduleMap = useMemo(() => schedules.reduce((acc, s) => { acc[s.id] = s; return acc; }, {}), [schedules]);
  // Build a company-id → name lookup from the reviewable companies we loaded
  // plus any company_name attached to the contact rows via the join.
  const companyNameMap = useMemo(() => {
    const m = {};
    for (const co of companies) m[co.id] = co.company_name;
    for (const ct of contacts) if (ct.company_id && ct.company_name) m[ct.company_id] = ct.company_name;
    return m;
  }, [companies, contacts]);
  const companyNameFor = (id) => companyNameMap[id] || null;

  const coNeverReviewed = useMemo(() => companies.filter(c => !c.last_reviewed_snapshot), [companies]);
  const coEditedRows    = useMemo(() => companies.filter(c =>  c.last_reviewed_snapshot), [companies]);
  const ctNeverReviewed = useMemo(() => contacts.filter(c => !c.last_reviewed_snapshot), [contacts]);
  const ctEditedRows    = useMemo(() => contacts.filter(c =>  c.last_reviewed_snapshot), [contacts]);

  const totalCount = companies.length + contacts.length;

  // --- Company approvals ----------------------------------------------------
  const approveCompany = async (id) => {
    setBusy(true);
    try {
      await apiFetch(`/companies/${id}/approve`, { method: "POST" });
      onApproved?.();
      load();
    } catch (e) { alert(parseErr(e, "Failed to approve")); setBusy(false); }
  };
  const approveCompaniesBulk = async (ids) => {
    if (ids.length === 0) return;
    if (!window.confirm(`Approve ${ids.length} ${ids.length === 1 ? "company" : "companies"} as reviewed?\n\nCurrent values will become the new baseline.`)) return;
    setBusy(true);
    try {
      const r = await apiFetch(`/companies/approve-bulk`, { method: "POST", body: { ids } });
      onApproved?.();
      if (r.not_found && r.not_found.length > 0) {
        alert(`Approved ${r.approved}. ${r.not_found.length} row(s) not found (likely deleted) — refreshing.`);
      }
      load();
    } catch (e) { alert(parseErr(e, "Failed to bulk approve")); setBusy(false); }
  };

  // --- Contact approvals (Chunk 7E) -----------------------------------------
  const approveContact = async (id) => {
    setBusy(true);
    try {
      await apiFetch(`/contacts/${id}/approve`, { method: "POST" });
      onApproved?.();
      load();
    } catch (e) { alert(parseErr(e, "Failed to approve contact")); setBusy(false); }
  };

  // Chunk RQ-step2 (2026-05-01): admin marks a contact as actually-an-org,
  // routes it to vca_contact_skipped + deletes the contact row.  Combined
  // with RQ-step1 in main.py, the row stays out of contacts on next sync.
  const routeContactToSkiplog = async (c) => {
    const label = `${c.first_name || ""} ${c.last_name || ""}`.trim() || `contact #${c.id}`;
    if (!window.confirm(`Mark "${label}" as an organization?\n\nThis will:\n  • Move it to the skip-log (review queue for orgs)\n  • Delete the contact row\n  • Prevent re-import on next VCA sync\n\nThis cannot be undone via the UI.`)) return;
    setBusy(true);
    try {
      await apiFetch(`/contacts/${c.id}/route-to-skiplog`, { method: "POST" });
      onApproved?.();
      load();
    } catch (e) { alert(parseErr(e, "Failed to route to skip-log")); setBusy(false); }
  };
  const approveContactsBulk = async (ids) => {
    if (ids.length === 0) return;
    if (!window.confirm(`Approve ${ids.length} ${ids.length === 1 ? "contact" : "contacts"} as reviewed?\n\nCurrent values will become the new baseline.`)) return;
    setBusy(true);
    try {
      const r = await apiFetch(`/contacts/approve-bulk`, { method: "POST", body: { ids } });
      onApproved?.();
      if (r.not_found && r.not_found.length > 0) {
        alert(`Approved ${r.approved}. ${r.not_found.length} row(s) not found (likely deleted) — refreshing.`);
      }
      load();
    } catch (e) { alert(parseErr(e, "Failed to bulk approve contacts")); setBusy(false); }
  };

  // Keep the per-section selection state pruned to ids that are still visible
  // after a reload. (If an id was selected and then approved, it'd vanish from
  // the list and stay in the selection set forever otherwise.)
  const prune = (sel, visibleIds) => {
    const out = new Set(); for (const id of sel) if (visibleIds.has(id)) out.add(id); return out;
  };
  useEffect(() => {
    const nIds = new Set(coNeverReviewed.map(r => r.id));
    const eIds = new Set(coEditedRows.map(r => r.id));
    setSelCoNever(prev => { const p = prune(prev, nIds); return p.size === prev.size ? prev : p; });
    setSelCoEdits(prev => { const p = prune(prev, eIds); return p.size === prev.size ? prev : p; });
    // eslint-disable-next-line
  }, [companies]);
  useEffect(() => {
    const nIds = new Set(ctNeverReviewed.map(r => r.id));
    const eIds = new Set(ctEditedRows.map(r => r.id));
    setSelCtNever(prev => { const p = prune(prev, nIds); return p.size === prev.size ? prev : p; });
    setSelCtEdits(prev => { const p = prune(prev, eIds); return p.size === prev.size ? prev : p; });
    // eslint-disable-next-line
  }, [contacts]);

  const toggleOne = (set, setter, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };
  const toggleAll = (rows, set, setter) => {
    if (set.size === rows.length) setter(new Set());
    else setter(new Set(rows.map(r => r.id)));
  };

  return (
    <div>
      <div className="section-header">
        <span className="section-title">
          Review Queue ({totalCount})
          {totalCount > 0 && (
            <span style={{marginLeft:10,fontSize:12,color:"var(--text3)",fontWeight:400}}>
              — {companies.length} {companies.length === 1 ? "company" : "companies"},
              {" "}{contacts.length} {contacts.length === 1 ? "contact" : "contacts"} pending
            </span>
          )}
        </span>
        <button className="btn btn-outline btn-sm" onClick={load} disabled={loading || busy}>Refresh</button>
      </div>

      {err && <div className="alert-error">{err}</div>}

      {loading ? (
        <div className="card" style={{padding:20}}><div className="loading">Loading review queue...</div></div>
      ) : totalCount === 0 ? (
        <div className="card" style={{padding:24,textAlign:"center",color:"var(--text2)"}}>
          <div style={{fontSize:32,marginBottom:8}}>✓</div>
          <div style={{fontWeight:600,marginBottom:4}}>Nothing to review.</div>
          <div style={{fontSize:12,color:"var(--text3)"}}>All companies and contacts are up to date.</div>
        </div>
      ) : (
        <>
          {/* =================== COMPANIES =================== */}
          {companies.length > 0 && (
            <div style={{marginBottom:24}}>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.5,margin:"4px 0 8px"}}>
                Companies ({companies.length})
              </div>

              {/* Edits since last review */}
              <div style={{marginBottom:16}}>
                <SectionHeader
                  open={coEditsOpen}
                  onToggle={() => setCoEditsOpen(o => !o)}
                  title="Edited Since Last Review"
                  count={coEditedRows.length}
                  hint={coEditedRows.length === 0 ? "No pending edits." : "Changes made by non-admins that haven't been approved yet."}
                  rightAction={coEditedRows.length > 0 && selCoEdits.size > 0 && (
                    <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => approveCompaniesBulk([...selCoEdits])}>
                      Approve Selected ({selCoEdits.size})
                    </button>
                  )}
                />
                {coEditsOpen && coEditedRows.length > 0 && (
                  <div className="card table-wrap" style={{borderTop:"none",borderRadius:"0 0 6px 6px"}}>
                    <table>
                      <thead><tr>
                        <th style={{width:32}}>
                          <input type="checkbox"
                            checked={selCoEdits.size === coEditedRows.length && coEditedRows.length > 0}
                            onChange={() => toggleAll(coEditedRows, selCoEdits, setSelCoEdits)}
                            title="Select all in this section"/>
                        </th>
                        <th>Company</th>
                        <th>Changes</th>
                        <th>Status</th>
                        <th className="text-right">Actions</th>
                      </tr></thead>
                      <tbody>{coEditedRows.map(c => {
                        const diffs = diffFields(c.last_reviewed_snapshot, c);
                        return (
                          <tr key={c.id} style={{background:"#fffbeb"}}>
                            <td><input type="checkbox" checked={selCoEdits.has(c.id)} onChange={() => toggleOne(selCoEdits, setSelCoEdits, c.id)}/></td>
                            <td style={{fontWeight:600,whiteSpace:"nowrap"}}>
                              {c.company_name}
                              {(c.city || c.state) && <div style={{fontSize:11,color:"var(--text3)",fontWeight:400}}>{[c.city, c.state].filter(Boolean).join(", ")}</div>}
                            </td>
                            <td>{diffs.length === 0
                              ? <span style={{fontSize:11,color:"var(--text3)"}}>(flagged but no field diffs — review anyway)</span>
                              : diffs.map((d, i) => <DiffChip key={i} diff={d} scheduleMap={scheduleMap}/>)}
                            </td>
                            <td>
                              {!c.is_active && <span className="badge" style={{background:"#f3f4f6",color:"#6b7280",marginRight:4}}>Disabled</span>}
                              <span className="badge" style={{background:"#fef3c7",color:"#92400e"}}>Pending</span>
                            </td>
                            <td className="text-right" style={{whiteSpace:"nowrap"}}>
                              <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => approveCompany(c.id)} style={{marginRight:4}}>Approve</button>
                              <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => setEditing(c)}>Edit</button>
                            </td>
                          </tr>
                        );
                      })}</tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Never reviewed */}
              <div>
                <SectionHeader
                  open={coNeverOpen}
                  onToggle={() => setCoNeverOpen(o => !o)}
                  title="Never Reviewed"
                  count={coNeverReviewed.length}
                  hint={coNeverReviewed.length === 0 ? "No new arrivals." : "Mostly VCA imports — bulk-approve once you've spot-checked them."}
                  rightAction={coNeverReviewed.length > 0 && selCoNever.size > 0 && (
                    <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => approveCompaniesBulk([...selCoNever])}>
                      Approve Selected ({selCoNever.size})
                    </button>
                  )}
                />
                {coNeverOpen && coNeverReviewed.length > 0 && (
                  <div className="card table-wrap" style={{borderTop:"none",borderRadius:"0 0 6px 6px"}}>
                    <table>
                      <thead><tr>
                        <th style={{width:32}}>
                          <input type="checkbox"
                            checked={selCoNever.size === coNeverReviewed.length && coNeverReviewed.length > 0}
                            onChange={() => toggleAll(coNeverReviewed, selCoNever, setSelCoNever)}
                            title="Select all in this section"/>
                        </th>
                        <SortableTh label="Name"     col="company_name" sort={{key:"company_name",dir:"asc"}} setSort={()=>{}} />
                        <th>Location</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>Source</th>
                        <th className="text-right">Actions</th>
                      </tr></thead>
                      <tbody>{coNeverReviewed.map(c => (
                        <tr key={c.id}>
                          <td><input type="checkbox" checked={selCoNever.has(c.id)} onChange={() => toggleOne(selCoNever, setSelCoNever, c.id)}/></td>
                          <td style={{fontWeight:600}}>{c.company_name}</td>
                          <td>{[c.city, c.state].filter(Boolean).join(", ") || <span style={{color:"var(--text3)"}}>—</span>}</td>
                          <td>{c.phone || <span style={{color:"var(--text3)"}}>—</span>}</td>
                          <td>{c.email || <span style={{color:"var(--text3)"}}>—</span>}</td>
                          <td>
                            {c.vca_last_seen_at
                              ? <span className="badge" style={{background:"#dbeafe",color:"#1e40af"}}>VCA import</span>
                              : <span className="badge" style={{background:"#f3f4f6",color:"#6b7280"}}>User-created</span>}
                          </td>
                          <td className="text-right" style={{whiteSpace:"nowrap"}}>
                            <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => approveCompany(c.id)} style={{marginRight:4}}>Approve</button>
                            <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => setEditing(c)}>Edit</button>
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* =================== CONTACTS (Chunk 7E) =================== */}
          {contacts.length > 0 && (
            <div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.5,margin:"4px 0 8px"}}>
                Contacts ({contacts.length})
              </div>

              {/* Edits since last review */}
              <div style={{marginBottom:16}}>
                <SectionHeader
                  open={ctEditsOpen}
                  onToggle={() => setCtEditsOpen(o => !o)}
                  title="Edited Since Last Review"
                  count={ctEditedRows.length}
                  hint={ctEditedRows.length === 0 ? "No pending edits." : "Changes made by non-admins that haven't been approved yet."}
                  rightAction={ctEditedRows.length > 0 && selCtEdits.size > 0 && (
                    <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => approveContactsBulk([...selCtEdits])}>
                      Approve Selected ({selCtEdits.size})
                    </button>
                  )}
                />
                {ctEditsOpen && ctEditedRows.length > 0 && (
                  <div className="card table-wrap" style={{borderTop:"none",borderRadius:"0 0 6px 6px"}}>
                    <table>
                      <thead><tr>
                        <th style={{width:32}}>
                          <input type="checkbox"
                            checked={selCtEdits.size === ctEditedRows.length && ctEditedRows.length > 0}
                            onChange={() => toggleAll(ctEditedRows, selCtEdits, setSelCtEdits)}
                            title="Select all in this section"/>
                        </th>
                        <th>Contact</th>
                        <th>Changes</th>
                        <th>Status</th>
                        <th className="text-right">Actions</th>
                      </tr></thead>
                      <tbody>{ctEditedRows.map(c => {
                        const diffs = diffFieldsContact(c.last_reviewed_snapshot, c);
                        return (
                          <tr key={c.id} style={{background:"#fffbeb"}}>
                            <td><input type="checkbox" checked={selCtEdits.has(c.id)} onChange={() => toggleOne(selCtEdits, setSelCtEdits, c.id)}/></td>
                            <td style={{fontWeight:600,whiteSpace:"nowrap"}}>
                              {contactDisplayName(c)}
                              {c.company_name && <div style={{fontSize:11,color:"var(--text3)",fontWeight:400}}>{c.company_name}</div>}
                            </td>
                            <td>{diffs.length === 0
                              ? <span style={{fontSize:11,color:"var(--text3)"}}>(flagged but no field diffs — review anyway)</span>
                              : diffs.map((d, i) => <ContactDiffChip key={i} diff={d} companyNameFor={companyNameFor}/>)}
                            </td>
                            <td><span className="badge" style={{background:"#fef3c7",color:"#92400e"}}>Pending</span></td>
                            <td className="text-right" style={{whiteSpace:"nowrap"}}>
                              <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => approveContact(c.id)} style={{marginRight:4}}>Approve</button>
                              <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => setEditingContact(c)} style={{marginRight:4}}>Edit</button>
                              <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => routeContactToSkiplog(c)} style={{color:"#991b1b",borderColor:"#991b1b"}} title="Mark as organization, route to skip-log, delete contact">Mark as Org</button>
                            </td>
                          </tr>
                        );
                      })}</tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Never reviewed */}
              <div>
                <SectionHeader
                  open={ctNeverOpen}
                  onToggle={() => setCtNeverOpen(o => !o)}
                  title="Never Reviewed"
                  count={ctNeverReviewed.length}
                  hint={ctNeverReviewed.length === 0 ? "No new arrivals." : "Mostly VCA imports (reps, TPA reps, per-claim contacts) — bulk-approve once you've spot-checked them."}
                  rightAction={ctNeverReviewed.length > 0 && selCtNever.size > 0 && (
                    <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => approveContactsBulk([...selCtNever])}>
                      Approve Selected ({selCtNever.size})
                    </button>
                  )}
                />
                {ctNeverOpen && ctNeverReviewed.length > 0 && (
                  <div className="card table-wrap" style={{borderTop:"none",borderRadius:"0 0 6px 6px"}}>
                    <table>
                      <thead><tr>
                        <th style={{width:32}}>
                          <input type="checkbox"
                            checked={selCtNever.size === ctNeverReviewed.length && ctNeverReviewed.length > 0}
                            onChange={() => toggleAll(ctNeverReviewed, selCtNever, setSelCtNever)}
                            title="Select all in this section"/>
                        </th>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Source</th>
                        <th className="text-right">Actions</th>
                      </tr></thead>
                      <tbody>{ctNeverReviewed.map(c => (
                        <tr key={c.id}>
                          <td><input type="checkbox" checked={selCtNever.has(c.id)} onChange={() => toggleOne(selCtNever, setSelCtNever, c.id)}/></td>
                          <td style={{fontWeight:600}}>{contactDisplayName(c)}</td>
                          <td>{c.company_name || <span style={{color:"var(--text3)"}}>—</span>}</td>
                          <td>{c.email || <span style={{color:"var(--text3)"}}>—</span>}</td>
                          <td>{c.phone || <span style={{color:"var(--text3)"}}>—</span>}</td>
                          <td>
                            {c.vca_last_seen_at
                              ? <span className="badge" style={{background:"#dbeafe",color:"#1e40af"}}>VCA import</span>
                              : <span className="badge" style={{background:"#f3f4f6",color:"#6b7280"}}>User-created</span>}
                          </td>
                          <td className="text-right" style={{whiteSpace:"nowrap"}}>
                            <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => approveContact(c.id)} style={{marginRight:4}}>Approve</button>
                            <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => setEditingContact(c)} style={{marginRight:4}}>Edit</button>
                            <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => routeContactToSkiplog(c)} style={{color:"#991b1b",borderColor:"#991b1b"}} title="Mark as organization, route to skip-log, delete contact">Mark as Org</button>
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {editing && (
        <EditCompanyModal
          company={editing}
          schedules={schedules}
          isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onApproved?.(); load(); }}
          onNavigateToClaim={onNavigateToClaim}
          onNavigateToContact={onNavigateToContact}
        />
      )}
      {editingContact && (
        <EditContactModal
          contact={editingContact}
          isAdmin={isAdmin}
          onClose={() => setEditingContact(null)}
          onSaved={() => { setEditingContact(null); onApproved?.(); load(); }}
          onNavigateToClaim={onNavigateToClaim}
        />
      )}
    </div>
  );
}

// ===================== Audit Vendors (Scope H) =====================
// Manage the medical-audit vendor list (LD-BILLUX-007 amendment). The list feeds the
// Send-to-Audit picker; the default vendor is the no-approval send; non-default sends
// require APPROVE_AUDIT_VENDOR. Gated by manage_bill_intake at the Admin sub-tab.
function AuditVendorsPanel() {
  const vInp = { fontSize: 13, padding: "5px 8px", border: "1px solid var(--border)", borderRadius: 5, fontFamily: "inherit" };
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", contact_email: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => { setLoading(true); apiFetch("/audit-vendors").then(d => { setVendors(d || []); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.name.trim() || busy) return;
    setBusy(true); setMsg(null);
    try {
      await apiFetch("/audit-vendors", { method: "POST", body: { name: form.name.trim(), contact_email: form.contact_email.trim() || null, notes: form.notes.trim() || null } });
      setForm({ name: "", contact_email: "", notes: "" }); load();
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setBusy(false); }
  };
  const patch = async (id, body) => { setBusy(true); setMsg(null); try { await apiFetch(`/audit-vendors/${id}`, { method: "PATCH", body }); load(); } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setBusy(false); } };
  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this vendor? It will no longer appear in the Send-to-Audit picker.")) return;
    setBusy(true); setMsg(null);
    try { await apiFetch(`/audit-vendors/${id}`, { method: "DELETE" }); load(); } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="section-header"><span className="section-title">Audit Vendors ({vendors.length})</span></div>
      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text3)", marginBottom: 6 }}>ADD VENDOR</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Vendor name" style={vInp} />
          <input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="Contact email" style={vInp} />
          <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes" style={{ ...vInp, flex: 1, minWidth: 160 }} />
          <button className="btn btn-blue btn-sm" disabled={busy || !form.name.trim()} onClick={add}>Add</button>
        </div>
      </div>
      {msg && <div style={{ color: "#991b1b", fontSize: 12, marginBottom: 8 }}>{msg}</div>}
      <div className="card table-wrap">
        {loading ? <div style={{ padding: 16 }}>Loading…</div> : (
          <table>
            <thead><tr><th>Name</th><th>Email</th><th>Notes</th><th>Default</th><th>Actions</th></tr></thead>
            <tbody>
              {vendors.map(v => (
                <tr key={v.id}>
                  <td>{v.name}</td>
                  <td>{v.contact_email || "—"}</td>
                  <td style={{ color: "var(--text2)", fontSize: 12 }}>{v.notes || ""}</td>
                  <td>{v.is_default ? <span style={{ color: "#166534", fontWeight: 700 }}>★ Default</span> : <button className="btn btn-sm btn-outline" disabled={busy} onClick={() => patch(v.id, { is_default: true })}>Set default</button>}</td>
                  <td><button className="btn btn-sm btn-outline" disabled={busy || v.is_default} title={v.is_default ? "Make another vendor the default first" : ""} onClick={() => deactivate(v.id)}>Deactivate</button></td>
                </tr>
              ))}
              {vendors.length === 0 && <tr><td colSpan={5} style={{ padding: 16, color: "var(--text3)" }}>No active vendors.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ===================== TRUSTS & PAYMENT METHODS (custom lists) =====================
// BILLS-WORKFLOW Chunk 1: configurable Trust entity (name, 2-letter QBO code, owner,
// kind, active) + Payment Method list (display + 1-letter code + active). Short codes
// drive the QBO Bill No. (e.g. GA-A). MANAGE_BILL_INTAKE.
function TrustsMethodsPanel() {
  const vInp = { fontSize: 13, padding: "5px 8px", border: "1px solid var(--border)", borderRadius: 5, fontFamily: "inherit" };
  const KINDS = [["ia_general", "IA / General"], ["markel_tpa", "Markel / TPA"], ["other", "Other"]];
  const [trusts, setTrusts] = useState([]);
  const [methods, setMethods] = useState([]);
  const [tForm, setTForm] = useState({ account_name: "", short_code: "", trust_account_kind: "ia_general" });
  const [mForm, setMForm] = useState({ name: "", short_code: "" });
  const [ownerQ, setOwnerQ] = useState({});     // {trustId: query}
  const [ownerHits, setOwnerHits] = useState({});
  const [genQ, setGenQ] = useState(""); const [genHits, setGenHits] = useState([]); const [genCo, setGenCo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => {
    apiFetch("/trust-accounts/manage").then(d => setTrusts(d || [])).catch(() => {});
    apiFetch("/payment-mechanisms?active_only=false").then(d => setMethods(d.payment_mechanisms || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const wrap = async (fn) => { setBusy(true); setMsg(null); try { await fn(); load(); } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setBusy(false); } };
  const addTrust = () => { if (!tForm.account_name.trim()) return; wrap(async () => { await apiFetch("/trust-accounts", { method: "POST", body: { account_name: tForm.account_name.trim(), short_code: tForm.short_code.trim() || null, trust_account_kind: tForm.trust_account_kind } }); setTForm({ account_name: "", short_code: "", trust_account_kind: "ia_general" }); }); };
  const patchTrust = (id, body) => wrap(() => apiFetch(`/trust-accounts/${id}`, { method: "PATCH", body }));
  const addMethod = () => { if (!mForm.name.trim()) return; wrap(async () => { await apiFetch("/payment-mechanisms", { method: "POST", body: { name: mForm.name.trim(), short_code: mForm.short_code.trim() || null } }); setMForm({ name: "", short_code: "" }); }); };
  const patchMethod = (id, body) => wrap(() => apiFetch(`/payment-mechanisms/${id}`, { method: "PATCH", body }));
  const searchOwner = (tid, q) => {
    setOwnerQ({ ...ownerQ, [tid]: q });
    if (!q || q.length < 2) { setOwnerHits({ ...ownerHits, [tid]: [] }); return; }
    apiFetch(`/companies?search=${encodeURIComponent(q)}&limit=8`).then(d => setOwnerHits({ ...ownerHits, [tid]: (d || []).slice(0, 8) })).catch(() => {});
  };
  return (
    <div>
      {msg && <div style={{ color: "#991b1b", fontSize: 12, marginBottom: 8 }}>{msg}</div>}
      <div className="section-header"><span className="section-title">Trust Accounts ({trusts.length})</span></div>
      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text3)", marginBottom: 6 }}>ADD TRUST ACCOUNT</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input value={tForm.account_name} onChange={e => setTForm({ ...tForm, account_name: e.target.value })} placeholder="Trust name (e.g. Golden Alaska Trust General)" style={{ ...vInp, flex: 1, minWidth: 220 }} />
          <input value={tForm.short_code} onChange={e => setTForm({ ...tForm, short_code: e.target.value.toUpperCase() })} placeholder="Code" maxLength={4} style={{ ...vInp, width: 70 }} />
          <select value={tForm.trust_account_kind} onChange={e => setTForm({ ...tForm, trust_account_kind: e.target.value })} style={vInp}>{KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <button className="btn btn-blue btn-sm" disabled={busy || !tForm.account_name.trim()} onClick={addTrust}>Add</button>
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 6 }}>The 2-letter code is the QBO Bill No. prefix (Golden Alaska + ACH → <span className="mono">GA-A</span>). Adjusters never see it.</div>
      </div>
      <div className="card table-wrap" style={{ marginBottom: 20 }}>
        <table>
          <thead><tr><th>Name</th><th>Code</th><th>Kind</th><th>Owner</th><th style={{ textAlign: "right" }}>Balance</th><th>Active</th></tr></thead>
          <tbody>
            {trusts.map(t => (
              <tr key={t.id}>
                <td><input defaultValue={t.account_name} style={{ ...vInp, width: "100%" }} onBlur={e => e.target.value.trim() && e.target.value !== t.account_name && patchTrust(t.id, { account_name: e.target.value.trim() })} /></td>
                <td><input defaultValue={t.short_code || ""} maxLength={4} style={{ ...vInp, width: 58 }} onBlur={e => e.target.value.toUpperCase() !== (t.short_code || "") && patchTrust(t.id, { short_code: e.target.value.toUpperCase() })} /></td>
                <td><select defaultValue={t.trust_account_kind || "other"} style={vInp} onChange={e => patchTrust(t.id, { trust_account_kind: e.target.value })}>{KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></td>
                <td style={{ position: "relative", minWidth: 180 }}>
                  <input value={ownerQ[t.id] ?? (t.owner_company_name || "")} placeholder="— none —" style={{ ...vInp, width: "100%" }} onChange={e => searchOwner(t.id, e.target.value)} />
                  {(ownerHits[t.id] || []).length > 0 && (
                    <div style={{ position: "absolute", zIndex: 30, background: "#fff", border: "1px solid var(--border)", borderRadius: 6, width: "100%", boxShadow: "0 6px 18px rgba(0,0,0,0.14)", maxHeight: 200, overflowY: "auto" }}>
                      {(ownerHits[t.id] || []).map(co => (
                        <div key={co.id} onClick={() => { patchTrust(t.id, { owner_company_id: co.id }); setOwnerQ({ ...ownerQ, [t.id]: co.company_name }); setOwnerHits({ ...ownerHits, [t.id]: [] }); }} style={{ padding: "5px 9px", cursor: "pointer", borderBottom: "1px solid var(--border-light,#f0f0f0)" }}>{co.company_name}</div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="mono" style={{ textAlign: "right" }}>{fmt.usd ? fmt.usd(t.balance) : `$${Number(t.balance || 0).toLocaleString()}`}</td>
                <td><input type="checkbox" checked={!!t.is_active} onChange={e => patchTrust(t.id, { is_active: e.target.checked })} /></td>
              </tr>
            ))}
            {trusts.length === 0 && <tr><td colSpan={6} style={{ padding: 16, color: "var(--text3)" }}>No trust accounts.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="section-header"><span className="section-title">Payment Methods ({methods.length})</span></div>
      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text3)", marginBottom: 6 }}>ADD PAYMENT METHOD</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input value={mForm.name} onChange={e => setMForm({ ...mForm, name: e.target.value })} placeholder="Display name (e.g. Wire)" style={{ ...vInp, minWidth: 200 }} />
          <input value={mForm.short_code} onChange={e => setMForm({ ...mForm, short_code: e.target.value.toUpperCase() })} placeholder="Code" maxLength={2} style={{ ...vInp, width: 60 }} />
          <button className="btn btn-blue btn-sm" disabled={busy || !mForm.name.trim()} onClick={addMethod}>Add</button>
        </div>
      </div>
      <div className="card table-wrap">
        <table>
          <thead><tr><th>Name</th><th>Code</th><th>Active</th></tr></thead>
          <tbody>
            {methods.map(m => (
              <tr key={m.id}>
                <td><input defaultValue={m.name} style={{ ...vInp, width: "100%" }} onBlur={e => e.target.value.trim() && e.target.value !== m.name && patchMethod(m.id, { name: e.target.value.trim() })} /></td>
                <td><input defaultValue={m.short_code || ""} maxLength={2} style={{ ...vInp, width: 50 }} onBlur={e => e.target.value.toUpperCase() !== (m.short_code || "") && patchMethod(m.id, { short_code: e.target.value.toUpperCase() })} /></td>
                <td><input type="checkbox" checked={!!m.is_active} onChange={e => patchMethod(m.id, { is_active: e.target.checked })} /></td>
              </tr>
            ))}
            {methods.length === 0 && <tr><td colSpan={3} style={{ padding: 16, color: "var(--text3)" }}>No payment methods.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="section-header" style={{ marginTop: 20 }}><span className="section-title">Company General Trusts</span></div>
      <div className="card" style={{ padding: 12 }}>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 8 }}>Link an insured company to the general trust it holds in WNW stewardship (spans its claims). Claims set to "General trust" draw on it, and adjusters see its balance at bill approval.</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative", minWidth: 240 }}>
            <input value={genCo ? genCo.company_name : genQ} placeholder="Search insured company…" style={vInp}
              onChange={e => { setGenCo(null); setGenQ(e.target.value); if (e.target.value.length >= 2) apiFetch(`/companies?search=${encodeURIComponent(e.target.value)}&limit=8`).then(d => setGenHits((d || []).slice(0, 8))).catch(() => {}); else setGenHits([]); }} />
            {!genCo && genHits.length > 0 && (
              <div style={{ position: "absolute", zIndex: 30, background: "#fff", border: "1px solid var(--border)", borderRadius: 6, width: "100%", boxShadow: "0 6px 18px rgba(0,0,0,0.14)", maxHeight: 200, overflowY: "auto" }}>
                {genHits.map(co => <div key={co.id} onClick={() => { setGenCo(co); setGenHits([]); }} style={{ padding: "5px 9px", cursor: "pointer", borderBottom: "1px solid var(--border-light,#f0f0f0)" }}>{co.company_name}{co.general_trust_account_id ? " ✓" : ""}</div>)}
              </div>
            )}
          </div>
          <select disabled={!genCo} value={(genCo && genCo.general_trust_account_id) || ""} style={vInp}
            onChange={e => { const tid = e.target.value; wrap(async () => { await apiFetch(`/companies/${genCo.id}/general-trust`, { method: "PATCH", body: { trust_account_id: tid ? parseInt(tid) : null } }); setGenCo({ ...genCo, general_trust_account_id: tid ? parseInt(tid) : null }); }); }}>
            <option value="">{genCo ? "— no general trust —" : "(pick a company)"}</option>
            {trusts.map(t => <option key={t.id} value={t.id}>{t.account_name}{t.short_code ? ` (${t.short_code})` : ""}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

// ===================== PAYEE VETTING (Chunk 4) =====================
// Adjusters create payees mid-flow; they land here PENDING for accounting/admin to
// approve (name/address/dup/W-9/payment pref). MANAGE_PAYEES.
function PayeeVettingPanel() {
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => apiFetch("/payees/pending").then(d => setPending(d.pending || [])).catch(e => setMsg(String(e.message || e)));
  useEffect(() => { load(); }, []);
  const vet = (p, decision) => {
    const notes = decision === "reject" ? (window.prompt("Reject — reason (optional):") ?? "") : (window.prompt("Approve — notes (optional):") ?? "");
    setBusy(true); setMsg(null);
    apiFetch(`/payees/${p.id}/vet`, { method: "POST", body: { decision, notes } })
      .then(load).catch(e => setMsg(String(e.message || e).slice(0, 200))).finally(() => setBusy(false));
  };
  return (
    <div>
      <div className="section-header"><span className="section-title">Payee Vetting — pending ({pending.length})</span></div>
      {msg && <div style={{ color: "#991b1b", fontSize: 12, marginBottom: 8 }}>{msg}</div>}
      <div className="card table-wrap">
        <table>
          <thead><tr><th>Payee</th><th>Kind</th><th>Created by</th><th>W-9</th><th>Possible duplicates</th><th>Actions</th></tr></thead>
          <tbody>
            {pending.map(p => (
              <tr key={p.id}>
                <td style={{ fontWeight: 600 }}>{p.entity_name || "(unnamed)"}</td>
                <td>{p.entity_kind}</td>
                <td style={{ fontSize: 12 }}>{p.created_by_name || "—"}</td>
                <td style={{ fontSize: 12 }}>{p.w9_status || "unknown_review"}</td>
                <td style={{ fontSize: 12, color: (p.dup_hints || []).length ? "#b45309" : "var(--text3)" }}>
                  {(p.dup_hints || []).length ? p.dup_hints.map(d => d.company_name).join(", ") : "none found"}
                </td>
                <td>
                  <button className="btn btn-sm btn-blue" disabled={busy} onClick={() => vet(p, "approve")}>Approve</button>
                  <button className="btn btn-sm btn-outline" style={{ marginLeft: 4 }} disabled={busy} onClick={() => vet(p, "reject")}>Reject</button>
                </td>
              </tr>
            ))}
            {pending.length === 0 && <tr><td colSpan={6} style={{ padding: 16, color: "var(--text3)" }}>No payees awaiting vetting.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===================== ADMIN PAGE (top-level) =====================
// Payment Request release gate (#14): firm-default + per-adjuster thresholds, the Pending
// Release queue (release action), and the below-threshold spot-check feed. MANAGE_BILL_INTAKE.
function PaymentRequestReleasePanel() {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [feed, setFeed] = useState([]);
  const [firm, setFirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [addUser, setAddUser] = useState(""); const [addAmt, setAddAmt] = useState("");
  const money = (v) => (v == null || v === "" ? "—" : "$" + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }));
  const load = () => {
    apiFetch("/admin/pr-release-thresholds").then(d => { setData(d); setFirm(d.firm_default == null ? "" : String(d.firm_default)); }).catch(e => setMsg(String(e.message || e)));
    apiFetch("/payment-request-queue/pending-release").then(d => setPending(d.pending || [])).catch(() => {});
    apiFetch("/payment-request-queue/below-threshold?limit=50").then(d => setFeed(d.feed || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const setThreshold = (user_id, amount) => {
    setBusy(true); setMsg(null);
    apiFetch("/admin/pr-release-thresholds", { method: "PUT", body: { user_id, threshold_amount: (amount === "" || amount == null) ? null : parseFloat(amount) } })
      .then(() => { load(); setMsg("Saved."); }).catch(e => setMsg(String(e.message || e).slice(0, 200))).finally(() => setBusy(false));
  };
  const releasePR = (rid) => { setBusy(true); apiFetch(`/payment-requests/${rid}/release`, { method: "POST", body: {} }).then(() => load()).catch(e => setMsg(String(e.message || e).slice(0, 200))).finally(() => setBusy(false)); };
  if (!data) return <div style={{ padding: 16 }}>Loading…</div>;
  const overrideUserIds = new Set((data.overrides || []).map(o => o.user_id));
  const addableUsers = (data.users || []).filter(u => !overrideUserIds.has(u.id));
  const cell = { padding: "4px 0" }; const tbl = { width: "100%", fontSize: 13, borderCollapse: "collapse" };
  return (
    <div>
      <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 10 }}>
        Payment Requests at or above an adjuster's release threshold need an Admin/Accounting <b>Release</b> before they send.
        Below the threshold they send with a soft warning and land in the spot-check feed. Blank threshold = gate OFF for that scope.
      </div>
      {msg && <div style={{ fontSize: 12, color: "#166534", marginBottom: 8 }}>{msg}</div>}
      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Firm-wide default threshold</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13 }}>$</span>
          <input type="number" step="0.01" value={firm} onChange={e => setFirm(e.target.value)} placeholder="(blank = gate off)" style={{ padding: "5px 8px", border: "1px solid var(--border)", borderRadius: 5, width: 160 }} />
          <button className="btn btn-sm btn-blue" disabled={busy} onClick={() => setThreshold(0, firm)}>Save default</button>
          {data.firm_default == null && <span style={{ fontSize: 12, color: "#b45309" }}>Gate is OFF — no firm default set.</span>}
        </div>
      </div>
      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Per-adjuster overrides</div>
        <table style={tbl}><tbody>
          {(data.overrides || []).map(o => (
            <tr key={o.user_id} style={{ borderTop: "1px solid var(--border)" }}>
              <td style={cell}>{o.full_name} <span style={{ color: "var(--text3)", fontSize: 11 }}>{o.role}</span></td>
              <td style={{ ...cell, textAlign: "right" }} className="mono">{money(o.threshold_amount)}</td>
              <td style={{ ...cell, textAlign: "right" }}><button className="btn btn-sm btn-outline" disabled={busy} onClick={() => setThreshold(o.user_id, "")}>Remove</button></td>
            </tr>
          ))}
        </tbody></table>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <select value={addUser} onChange={e => setAddUser(e.target.value)} style={{ padding: "5px 8px", border: "1px solid var(--border)", borderRadius: 5 }}>
            <option value="">Add adjuster…</option>
            {addableUsers.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>)}
          </select>
          <span style={{ fontSize: 13 }}>$</span>
          <input type="number" step="0.01" value={addAmt} onChange={e => setAddAmt(e.target.value)} placeholder="threshold" style={{ padding: "5px 8px", border: "1px solid var(--border)", borderRadius: 5, width: 120 }} />
          <button className="btn btn-sm btn-blue" disabled={busy || !addUser || addAmt === ""} onClick={() => { setThreshold(parseInt(addUser), addAmt); setAddUser(""); setAddAmt(""); }}>Add</button>
        </div>
      </div>
      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Pending Release ({pending.length})</div>
        {pending.length === 0 ? <div style={{ fontSize: 12, color: "var(--text3)" }}>Nothing awaiting release.</div> :
          <table style={tbl}><tbody>
            {pending.map(p => (
              <tr key={p.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={cell}><span className="mono">{p.claim_number || "—"}</span> · {p.payee || "—"} <span style={{ color: "var(--text3)", fontSize: 11 }}>· {p.requested_payer}{p.created_by_name ? ` · by ${p.created_by_name}` : ""}</span></td>
                <td style={{ ...cell, textAlign: "right" }} className="mono">{money(p.amount)}</td>
                <td style={{ ...cell, textAlign: "right" }}><button className="btn btn-sm btn-blue" disabled={busy} onClick={() => releasePR(p.id)}>Release</button></td>
              </tr>
            ))}
          </tbody></table>}
      </div>
      <div className="card" style={{ padding: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Sent below threshold — spot-check ({feed.length})</div>
        {feed.length === 0 ? <div style={{ fontSize: 12, color: "var(--text3)" }}>None.</div> :
          <table style={tbl}><tbody>
            {feed.map(p => (
              <tr key={p.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={cell}><span className="mono">{p.claim_number || "—"}</span> · {p.payee || "—"} <span style={{ color: "var(--text3)", fontSize: 11 }}>{p.sent_by_name ? `· by ${p.sent_by_name}` : ""}</span></td>
                <td style={{ ...cell, textAlign: "right" }} className="mono">{money(p.amount)}</td>
              </tr>
            ))}
          </tbody></table>}
      </div>
    </div>
  );
}

export default function Admin({ currentUser, onReviewCountChanged, onNavigateToClaim, onNavigateToContact, onReconciled }) {
  // Per-sub-tab capability gates. An Accountant with only
  // manage_billing_rates sees just Billing Rates + Rate Schedules; a Claims
  // Manager with only approve_companies/contacts sees just Review Queue;
  // a system_admin sees everything including the System group.
  const canManageUsers   = can("manage_users");
  const canBillingRates  = can("manage_billing_rates");
  const canReview        = canAny(["approve_companies", "approve_contacts"]);
  // UI-NAV-1.5 (2026-05-07): System group caps lifted from SystemAdmin.js.
  // Each sub-tab keeps its own gate so e.g. ownership sees a sparse System
  // group if they only have view-only access to certain sub-surfaces.
  const canVca           = canAny(["manage_vca_sync", "manage_pipeline"]);
  const canAudit         = can("manage_users");
  const canSysAccess     = can("view_sysadmin_panel");
  const canBillIntake    = can("manage_bill_intake");  // Scope H: audit-vendors admin
  const canPayees        = can("manage_payees");       // Chunk 4: payee vetting queue
  const canWebPublish    = can("manage_website_publish"); // EMPLOYEE-PROFILE-1: website publish queue

  const firstVisibleTab  = canManageUsers ? "users"
                         : canBillingRates ? "rates"
                         : canReview ? "review"
                         : canVca ? "vca"
                         : canAudit ? "audit"
                         : canSysAccess ? "project_status"
                         : "users";  // fallback — user shouldn't reach this page at all
  const [tab, setTab] = useState(firstVisibleTab);
  const [reviewCount, setReviewCount] = useState(null);
  const [anomalyCount, setAnomalyCount] = useState(null);  // VCA Data tab red bubble
  const [vcaRefreshCount, setVcaRefreshCount] = useState(null);  // VCA Data tab amber bubble
  const [webPublishCount, setWebPublishCount] = useState(null);  // pending profile publishes

  const fetchCount = () => {
    if (!canReview) { setReviewCount(0); return; }
    apiFetch("/admin/review-count")
      .then(r => setReviewCount((r.companies ?? 0) + (r.contacts ?? 0)))
      .catch(() => {});
  };

  // VCA Data tab red bubble — counts unacknowledged sync anomalies AND
  // adds 1 if the most recent reconcile run errored.  Lifted verbatim from
  // SystemAdmin.js (Chunk RA pattern).
  const fetchAnomalyCount = () => {
    if (!canVca) { setAnomalyCount(0); return; }
    Promise.allSettled([
      apiFetch("/admin/sync-anomalies/unacknowledged-count"),
      apiFetch("/admin/project-status/last-reconcile-status"),
    ]).then(([a, r]) => {
      const anom = a.status === "fulfilled" ? (a.value?.count ?? 0) : 0;
      const reconcileFailed =
        r.status === "fulfilled" &&
        (r.value?.status === "error" || (r.value?.status === "success" && r.value?.is_stale));
      setAnomalyCount(anom + (reconcileFailed ? 1 : 0));
    });
  };

  // VCA Data tab AMBER bubble — claims whose Payment History can be refreshed from VCA
  // (vca_ph_autofetch.py; TRANSITIONAL, retires at VCA cutover).  Deliberately NOT summed
  // into the red anomaly bubble: red means "something is wrong over there", this means
  // "there is routine work waiting".  manage_vca_sync only — the endpoint 403s
  // manage_pipeline-only users, who also don't get the button.
  const fetchVcaRefreshCount = () => {
    if (!can("manage_vca_sync")) { setVcaRefreshCount(0); return; }
    apiFetch("/admin/vca-import/auto/count")
      .then(r => setVcaRefreshCount(r.refreshable ?? 0))
      .catch(() => {});
  };

  // Website Publish tab amber bubble — employees waiting on profile approval.
  // Discoverability fix (Kuhrt asked twice where approvals live).
  const fetchWebPublishCount = () => {
    if (!canWebPublish) { setWebPublishCount(0); return; }
    apiFetch("/admin/website-publish/requests?status=pending")
      .then(r => setWebPublishCount((r.requests || []).length))
      .catch(() => {});
  };

  useEffect(() => { fetchCount(); fetchAnomalyCount(); fetchVcaRefreshCount(); fetchWebPublishCount(); }, [tab]);

  const handleApproved = () => {
    fetchCount();
    onReviewCountChanged?.();
  };

  return (
    <div className="page">
      <div className="tabs">
        {/* People & Access */}
        {canManageUsers  && <button className={`tab-btn ${tab==="users"?"active":""}`}      onClick={() => setTab("users")}>Users</button>}
        {/* Rates */}
        {canBillingRates && <button className={`tab-btn ${tab==="rates"?"active":""}`}      onClick={() => setTab("rates")}>Billing Rates</button>}
        {canBillingRates && <button className={`tab-btn ${tab==="schedules"?"active":""}`}  onClick={() => setTab("schedules")}>Rate Schedules</button>}
        {/* Review */}
        {canReview       && <button className={`tab-btn ${tab==="review"?"active":""}`}     onClick={() => setTab("review")}>
          Review Queue
          {reviewCount > 0 && (
            <span style={{marginLeft:6,padding:"1px 7px",borderRadius:10,background:"#f59e0b",color:"#fff",fontSize:11,fontWeight:600}}>
              {reviewCount}
            </span>
          )}
        </button>}
        {/* System (UI-NAV-1.5: folded in from former SystemAdmin compartment) */}
        {canVca && (
          <button className={`tab-btn ${tab==="vca"?"active":""}`} onClick={() => setTab("vca")}>
            VCA Data
            {anomalyCount > 0 && (
              <span style={{marginLeft:6,padding:"1px 7px",borderRadius:10,background:"#dc2626",color:"#fff",fontSize:11,fontWeight:600}}>
                {anomalyCount}
              </span>
            )}
            {vcaRefreshCount > 0 && (
              <span title="Claims whose payment history can be refreshed from VCA"
                    style={{marginLeft:6,padding:"1px 7px",borderRadius:10,background:"#f59e0b",color:"#fff",fontSize:11,fontWeight:600}}>
                {vcaRefreshCount}
              </span>
            )}
          </button>
        )}
        {canBillIntake && <button className={`tab-btn ${tab==="trusts_methods"?"active":""}`}    onClick={() => setTab("trusts_methods")}>Trusts &amp; Methods</button>}
        {canPayees    && <button className={`tab-btn ${tab==="payee_vetting"?"active":""}`}     onClick={() => setTab("payee_vetting")}>Payee Vetting</button>}
        {canWebPublish && <button className={`tab-btn ${tab==="website_publish"?"active":""}`}  onClick={() => setTab("website_publish")}>
          Website Publish
          {webPublishCount > 0 && (
            <span style={{marginLeft:6,padding:"1px 7px",borderRadius:10,background:"#f59e0b",color:"#fff",fontSize:11,fontWeight:600}}>
              {webPublishCount}
            </span>
          )}
        </button>}
        {canBillIntake && <button className={`tab-btn ${tab==="pr_release"?"active":""}`}        onClick={() => setTab("pr_release")}>PR Release</button>}
        {canBillIntake && <button className={`tab-btn ${tab==="audit_vendors"?"active":""}`}    onClick={() => setTab("audit_vendors")}>Audit Vendors</button>}
        {canBillIntake && <button className={`tab-btn ${tab==="doc_folders"?"active":""}`}      onClick={() => setTab("doc_folders")}>Document Folders</button>}
        {canAudit     && <button className={`tab-btn ${tab==="audit"?"active":""}`}           onClick={() => setTab("audit")}>Audit Log</button>}
        {canSysAccess && <button className={`tab-btn ${tab==="project_status"?"active":""}`}  onClick={() => setTab("project_status")}>Project Status</button>}
        {canSysAccess && <button className={`tab-btn ${tab==="table_editor"?"active":""}`}    onClick={() => setTab("table_editor")}>Table Editor</button>}
        {canSysAccess && <button className={`tab-btn ${tab==="system_settings"?"active":""}`} onClick={() => setTab("system_settings")}>System Settings</button>}
      </div>
      {tab === "users"     && canManageUsers  && <UsersPanel currentUser={currentUser}/>}
      {tab === "rates"     && canBillingRates && <BillingRatesPanel/>}
      {tab === "schedules" && canBillingRates && <RateSchedulesPanel/>}
      {tab === "review"    && canReview       && <ReviewQueuePanel currentUser={currentUser} onApproved={handleApproved} onNavigateToClaim={onNavigateToClaim} onNavigateToContact={onNavigateToContact}/>}
      {tab === "vca" && canVca && (
        <>
          <SyncAnomaliesPanel onChanged={fetchAnomalyCount}/>
          <VcaContactSkippedPanel/>
          <PipelinePanel onReconciled={onReconciled}/>
          <VcaPaymentImport/>
        </>
      )}
      {tab === "trusts_methods"  && canBillIntake && <TrustsMethodsPanel/>}
      {tab === "payee_vetting"   && canPayees     && <PayeeVettingPanel/>}
      {tab === "website_publish" && canWebPublish && <WebsitePublishPanel/>}
      {tab === "pr_release"      && canBillIntake && <PaymentRequestReleasePanel/>}
      {tab === "audit_vendors"   && canBillIntake && <AuditVendorsPanel/>}
      {tab === "doc_folders"     && canBillIntake && <AdminFolders/>}
      {tab === "audit"           && canAudit     && <AuditLogPanel onNavigateToClaim={onNavigateToClaim}/>}
      {tab === "project_status"  && canSysAccess && <ProjectStatusPanel/>}
      {tab === "table_editor"    && canSysAccess && <TableEditor/>}
      {tab === "system_settings" && canSysAccess && <SystemSettings/>}
    </div>
  );
}

